import { Stage, Layer, Text, Rect, Group, FastLayer} from 'react-konva';
import Konva from 'konva';
import { Children, cloneElement, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useGesture } from '@use-gesture/react';
import { RenderPrimitiveAsKonva, finalizeImages, mixHex, renderIndicators, createRunningPulseChip } from './RenderHelpers';
import { useRunningAnimation } from './@components/RunningAnimation';
import { exportKonvaToPptx } from './PptHelper';
import MainStore from './MainStore';
import { AvoidLib } from 'libavoid-js';
import CustomImage, { calcInheritedBounds } from './CustomImage';
import CustomText from './CustomText';
import RoundedArrow from './RoundedArrow';
import { OrthogonalConnector } from './router';

Konva.autoDrawEnabled = false

const updateLinksDuringMove = true
const linkArrowSize = 8
const SINGLE_FRAME_EPSILON = 0.001
const isHeightDebugEnabled = () => (typeof window !== "undefined" && window.__DEBUG_TIMELINE_HEIGHT === true);

function getEffectiveBackgroundRGBA(el) {
  const isTransparent = (c) =>
    !c || c === 'transparent' || c === 'rgba(0, 0, 0, 0)' || c === 'rgba(0,0,0,0)';

  let node = el;
  while (node && node !== document) {
    const cs = getComputedStyle(node);
    // If there’s a background image/gradient, there isn’t a single flat color.
    if (cs.backgroundImage && cs.backgroundImage !== 'none') {
      // You could decide to return null to signal “not a flat color”.
      return null;
    }
    if (!isTransparent(cs.backgroundColor)) {
      return cs.backgroundColor; // e.g. "rgb(255, 255, 255)"
    }
    node = node.parentElement;
  }

  // Fallbacks
  const body = getComputedStyle(document.body);
  if (!isTransparent(body.backgroundColor)) return body.backgroundColor;

  const html = getComputedStyle(document.documentElement);
  if (!isTransparent(html.backgroundColor)) return html.backgroundColor;

  // Last resort: white
  return 'rgb(255, 255, 255)';
}

function rgbStringToHex(rgbString) {
  // Handles formats like "rgb(255, 255, 255)" or "rgba(255, 255, 255, 0.8)"
  const match = rgbString.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
  if (!match) return null;

  const [_, r, g, b, a] = match;
  const toHex = (n) => Number(n).toString(16).padStart(2, '0');

  if (a !== undefined) {
    // If alpha < 1, include it as a two-digit hex (0–255)
    const alpha = Math.round(parseFloat(a) * 255);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha)}`;
  } else {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}

function applyCustomClipMask(context, rect) {
    const {scale, clipBox} = calcInheritedBounds( rect )

    if( clipBox){
        context.beginPath();
        context.rect(clipBox.x, clipBox.y, clipBox.width, clipBox.height)
        context.clip();
    }
}

// patch Konva.Rect's internal scene function once
if (!Konva.Rect.prototype.__clip_patched) {
  const originalSceneFunc = Konva.Rect.prototype._sceneFunc;
  Konva.Rect.prototype._sceneFunc = function (context) {
    context.save();
    applyCustomClipMask(context, this);
    originalSceneFunc.call(this, context);
    context.restore();
  };
  Konva.Rect.prototype.__clip_patched = true;
}

const isSafari = (() => {
  const ua = navigator.userAgent;
  return (
    ua.includes('Safari') &&
    !ua.includes('Chrome') &&
    !ua.includes('Chromium') &&
    !ua.includes('Android')
  );
})();


//export default function InfiniteCanvas(props){
const InfiniteCanvas = forwardRef(function InfiniteCanvas(props, ref){

    const childRefs = useRef([]);

    const attachRefs = (child, index) => {
        if (!childRefs.current[index]) {
          // Assign a function to the ref array if it doesn't exist
          childRefs.current[index] = {};
        }
    
        console.log(`attaching ${index}`)
        return cloneElement(child, {
          ref: (el) => {
            childRefs.current[index] = el;
          },
        });
      };

    const enablePages =  true
    const enableFlipping = true
    const enableNodePruning = true
    const enableZoom = props.enableZoom !== false
    const enableYPan = props.enablePan === undefined || props.enablePan == "vertical"
    const enableXPan = props.enablePan === undefined || props.enablePan == "horizontal"
    const enableDrag = props.enableDrag !== false
    const useOverlapClipMask = false//props.useOverlapClipMask !== false

    const scaleTriggers = {min: 0.1, max: 6}

    const colors = {
        hover:{
            background:{
                fill:'#d1fae5'
                
            },
            border:{
                stroke: "#a3f0c6",
                fill : '#a3f0c633'
            }
        },
        drop_hover:{
            background:{
                fill:'#a3f0c644'
                //fill:'#f9fafb'
            }
        },
        select:{
            background:{
                //fill : '#9abef822'
                fill:'#f3f6fd'
            },
            border:{
                stroke: "#9abef8",
                fill : '#9abef833'
            }
        }
    }
    
    const stageRef = useRef()
    const frameRef = useRef()
    const layerRef = useRef()
    const lineLayerRef = useRef()
    const textMeasureCtxRef = useRef(null)
    const runDashOffsetRef = useRef(0)
    const myState = useRef({renderList: props.render})
    useRunningAnimation(layerRef, runDashOffsetRef)
    myState.current.stageRectDirty ??= true
    const [fontsReady, setFontsReady] = useState(false)

    if( !fontsReady ){
        const weights = ["", 300, 400, 500 , 600, 700, 900]
        Promise.all(
             weights.flatMap(d=>
                [
                    `${d} 1em Poppins`,
                    `${d} 1em Poppins italic`,
                    `${d} 1em Poppins normal`,
                    `${d} 1em Poppins bold`,

                ]).map(d=>document.fonts.load(d))
        ).then(() => {
            setFontsReady(true);
        });
    }

    useEffect(() => {
        cleanUpRouting()
        return () => {
            const router = myState.current.routing?.router
            if (router && typeof router.shutdown === 'function') {
                cleanUpRouting()
                myState.current.routing = undefined
            }
        };
      }, []);
    useEffect(() => {
        if (props.clearTextSelectionKey !== undefined) {
            clearTextSelection()
        }
    }, [props.clearTextSelectionKey])
    /*
    useEffect(()=>{
        refreshLinks()
    }, [props.frameLinks])*/
    useEffect(() => {
        if (!props.enableTextSelection) {
            return
        }
        const handler = (e) => handleCopySelection(e)
        window.addEventListener("keydown", handler)
        return () => {
            window.removeEventListener("keydown", handler)
        }
    }, [props.enableTextSelection])

    const chunkWidth = 800
    const chunkHeight = 800

    function cleanUpRouting(){
        if( myState.current?.routing?.router ){
            myState.current.routing.router.shutdown();
            if(myState.current.frames){
                for(const frame of myState.current.frames){
                    removeRoutingForFrame(frame)
                }
            }
        }
    }

    const frameData = (id)=>{
        if(!myState.current?.frames){
            return undefined
        }
        const frame = myState.current.frames.find(d=>d.id === id)
        return frame
    }
    const framePosition = (id)=>{
        if(!myState.current?.frames){
            return []
        }
        const list = id ? myState.current.frames.filter(d=>d.id === id)  : myState.current.frames


        const mapped = list.map(d=>{
            const tl = convertSceneCoordToScreen( d.x, d.y)
            const br = convertSceneCoordToScreen( d.x + (d.node.attrs.width * d.node.attrs.scaleX), d.y + (d.node.attrs.height * d.node.attrs.scaleY))
                
            const positions = {
                id: d.id,
                scene:{
                    x: d.x,
                    y: d.y,
                    width: d.node.attrs.width,
                    height: d.node.attrs.height,
                    l: d.x,
                    r: d.x + (d.node.attrs.width  * d.node.attrs.scaleX),
                    t: d.y,
                    b: d.y + (d.node.attrs.height * d.node.attrs.scaleY),
                    s: d.node.attrs.scaleX
                },
                viewport:{
                    l: tl[0],
                    t: tl[1],
                    r: br[0],
                    b: br[1]
                }
            }

            if( d.parentRender ){
                let {x:px, y:py, s:ps} = getFramePosition(d.parentRender)
                positions.relative = {
                    x: (positions.scene.x - px) / ps,
                    y:  (positions.scene.y - py) / ps,
                    s: positions.scene.s / ps,
                }
            }

            return positions
        })
        return id ? mapped[0] : mapped
    }
    const exportToPptx = ()=>{
        console.log(`EXPORTING`)

        if( stageRef.current ){
            exportKonvaToPptx( stageRef.current )
        }
    }
    function getSelection(key){
        return myState.current.selected?.[key] ?? []
    }
    function frameList(){
        return myState.current?.frames?.map(d=>d.id) ?? []
    }
    function addFrame(d){
        if(!d){return}
        const existing = myState.current.renderList.find(d2=>d2.id === d.id)
        if( !existing ){
            myState.current.renderList.push(d)
        }
        refreshFrame(d.id)
    }
    function removeFrame(frameId){

        const existing = myState.current.frames?.find(d=>d.id === frameId) 
        if( existing ){
            removeRoutingForFrame( existing )
            if( myState.current.frameSelect && myState.current.selected.frame){
                const ids = myState.current.selected.frame.map(d=>d.attrs.id)
                if( ids.includes(frameId) ){
                    destroyFrameSelect()
                }
            }
            existing.node.children = existing.allNodes
            myState.current.frames = myState.current.frames.filter(d=>d.id !== frameId) 

            existing.node.children.forEach(d=>d.attrs.removing = true)
            existing.node.remove()

        }
        myState.current.renderList = myState.current.renderList.filter(d=>d?.id !== frameId)
        refreshLinks()
        stageRef.current.batchDraw()

    }
    function selectFrame( frameId ){
        const ids = myState.current?.selected?.frame?.map(d=>d.attrs.id)
        if( !ids || ids.length > 1 || ids[0]!== frameId){
            destroyFrameSelect()
            const frame = getFrame( frameId)
            if( frame ){
                createFrameSelect( frame.node )
            }
        }
    }

    function markStageRectDirty(){
        myState.current.stageRectDirty = true
    }

    function getCachedStageRect(){
        if(!stageRef.current){
            return undefined
        }

        const metrics = {
            scrollX: window.scrollX ?? window.pageXOffset ?? 0,
            scrollY: window.scrollY ?? window.pageYOffset ?? 0,
            innerWidth: window.innerWidth ?? 0,
            innerHeight: window.innerHeight ?? 0
        }

        const lastMetrics = myState.current.stageRectViewport
        if( lastMetrics ){
            if(
                lastMetrics.scrollX !== metrics.scrollX ||
                lastMetrics.scrollY !== metrics.scrollY ||
                lastMetrics.innerWidth !== metrics.innerWidth ||
                lastMetrics.innerHeight !== metrics.innerHeight
            ){
                myState.current.stageRectDirty = true
            }
        }

        if( myState.current.stageRectDirty || !myState.current.stageRect ){
            const container = stageRef.current.container?.()
            if( container ){
                myState.current.stageRect = container.getBoundingClientRect()
                myState.current.stageRectViewport = metrics
                myState.current.stageRectDirty = false
            }
        }

        return myState.current.stageRect
    }

    useImperativeHandle(ref, () => {
        return {
            exportToPptx,
            addFrame,
            updateFramePosition,
            clearHighlights: clearHightlights,
            removeFrame,
            restoreChildren,
            restoreChildrenForTracking,
            orderNestedFrames,
            framePosition,
            frameList,
            updateIndicators,
            updateActiveChip,
            selectFrame,
            frameData,
            stageNode: ()=>stageRef.current,
            size: ()=>[myState.current.width,myState.current.height],
            updateLinks,
            getLinks,
            refreshFrame,
            getSelection,
            convertEventToScene,
            convertClientCoords:({x,y})=>convertEventToScene({clientX: x, clientY: y})
        };
      }, []);


    const test_items = useMemo(()=>{
        const out = []
        
        let cols = 200, rows = 50
        
        for(let i = 0; i < (cols * rows); i++){
            out.push({x: (i % cols), y: Math.floor(i / cols)})
        }
        return out
    }, [])
    useEffect(()=>{
        return ()=>{
            if( myState.current.animationRequest ){
                console.log(`stopping anim ${myState.current.animationRequest}`)
                cancelAnimationFrame( myState.current.animationRequest )
            }
        }
    },[])

    function getNodeRect(node) {
        if (!node || typeof node.getClientRect !== 'function') {
            return null;
        }
        try {
            const rect = node.getClientRect();
            if (!rect || !Number.isFinite(rect.width) || !Number.isFinite(rect.height)) {
                return null;
            }
            return rect;
        } catch (err) {
            return null;
        }
    }

    function rectsOverlap(a, b) {
        if (!a || !b) {
            return false;
        }
        if (a.width <= 0 || a.height <= 0 || b.width <= 0 || b.height <= 0) {
            return false;
        }
        const ax2 = a.x + a.width;
        const ay2 = a.y + a.height;
        const bx2 = b.x + b.width;
        const by2 = b.y + b.height;
        return a.x < bx2 && ax2 > b.x && a.y < by2 && ay2 > b.y;
    }

    function collectNodesRenderedAfter(node) {
        const result = new Set();
        if (!node || typeof node.getParent !== 'function') {
            return result;
        }
        const baseRect = getNodeRect(node);
        if (!baseRect) {
            return result;
        }
        const stageNode = stageRef.current;
        let current = node;
        while (current && typeof current.getParent === 'function') {
            const parent = current.getParent();
            if (!parent) {
                break;
            }
            if (parent.attrs?.removing) {
                break;
            }
            let siblings = [];
            if (typeof parent.getChildren === 'function') {
                const collection = parent.getChildren();
                if (Array.isArray(collection)) {
                    siblings = collection;
                } else if (collection?.toArray) {
                    siblings = collection.toArray();
                }
            }
            const idx = siblings.indexOf(current);
            if (idx > -1) {
                for (let i = idx + 1; i < siblings.length; i++) {
                    const sibling = siblings[i];
                    if (!sibling || sibling === stageNode) {
                        continue;
                    }
                    if (sibling.attrs?.removing) {
                        continue;
                    }
                    const siblingRect = getNodeRect(sibling);
                    if (!rectsOverlap(baseRect, siblingRect)) {
                        continue;
                    }
                    result.add(sibling);
                }
            }
            current = parent;
        }
        return result;
    }

    function scheduleOrderingRefresh(nodesToRefresh) {
        if (!nodesToRefresh || nodesToRefresh.size === 0) {
            return;
        }
        const store =
            myState.current.pendingOrderRefresh ??
            (myState.current.pendingOrderRefresh = new Set());
        nodesToRefresh.forEach((node) => {
            if (!node || node === stageRef.current || node.attrs?.removing) {
                return;
            }
            store.add(node);
        });
        if (!store.size) {
            return;
        }
        if (myState.current.orderRefreshScheduled) {
            return;
        }
        myState.current.orderRefreshScheduled = true;
        requestAnimationFrame(() => {
            myState.current.orderRefreshScheduled = false;
            const pending = myState.current.pendingOrderRefresh;
            myState.current.pendingOrderRefresh = new Set();
            if (!pending || pending.size === 0) {
                return;
            }
            const ordered = Array.from(pending).sort((a, b) => {
                const az = typeof a.getAbsoluteZIndex === 'function' ? a.getAbsoluteZIndex() : 0;
                const bz = typeof b.getAbsoluteZIndex === 'function' ? b.getAbsoluteZIndex() : 0;
                return az - bz;
            });
            ordered.forEach((node) => {
                if (!node || node === stageRef.current || node.attrs?.removing) {
                    return;
                }
                const layer = typeof node.getLayer === 'function' ? node.getLayer() : null;
                if (!layer) {
                    return;
                }
                if (typeof node.draw === 'function') {
                    node.draw();
                } else if (typeof layer.draw === 'function') {
                    layer.draw();
                }
            });
        });
    }

    function subtractRegion(base, cut) {
        const ix = Math.max(base.x, cut.x);
        const iy = Math.max(base.y, cut.y);
        const ix2 = Math.min(base.x + base.width, cut.x + cut.width);
        const iy2 = Math.min(base.y + base.height, cut.y + cut.height);
        if (ix >= ix2 || iy >= iy2) {
            return [base];
        }
        const next = [];
        const baseRight = base.x + base.width;
        const baseBottom = base.y + base.height;
        if (iy > base.y) {
            next.push({
                x: base.x,
                y: base.y,
                width: base.width,
                height: iy - base.y,
            });
        }
        if (iy2 < baseBottom) {
            next.push({
                x: base.x,
                y: iy2,
                width: base.width,
                height: baseBottom - iy2,
            });
        }
        if (ix > base.x) {
            next.push({
                x: base.x,
                y: iy,
                width: ix - base.x,
                height: iy2 - iy,
            });
        }
        if (ix2 < baseRight) {
            next.push({
                x: ix2,
                y: iy,
                width: baseRight - ix2,
                height: iy2 - iy,
            });
        }
        return next.filter((rect) => rect.width > 0 && rect.height > 0);
    }

    function drawNodeWithOverlapMask(node, overlapNodes) {
        if (!node || !overlapNodes || overlapNodes.size === 0) {
            return false;
        }
        const layer = typeof node.getLayer === 'function' ? node.getLayer() : null;
        if (!layer) {
            return false;
        }
        const context = typeof layer.getContext === 'function' ? layer.getContext() : null;
        const canvas = typeof layer.getCanvas === 'function' ? layer.getCanvas() : null;
        const ctx = context?._context ?? canvas?.getContext?.('2d');
        if (!ctx || !canvas) {
            return false;
        }
        const nodeRect = getNodeRect(node);
        if (!nodeRect) {
            return false;
        }
        let visibleRegions = [nodeRect];
        overlapNodes.forEach((overlapNode) => {
            const rect = getNodeRect(overlapNode);
            if (!rect || rect.width <= 0 || rect.height <= 0) {
                return;
            }
            const next = [];
            for (const region of visibleRegions) {
                next.push(...subtractRegion(region, rect));
            }
            visibleRegions = next;
        });
        visibleRegions = visibleRegions.filter((rect) => rect.width > 0 && rect.height > 0);
        if (!visibleRegions.length) {
            return false;
        }
        ctx.save();
        ctx.beginPath();
        visibleRegions.forEach((rect) => {
            ctx.rect(rect.x, rect.y, rect.width, rect.height);
        });
        if (typeof ctx.clip === 'function') {
            ctx.clip();
        } else {
            ctx.restore();
            return false;
        }
        if (typeof node.draw === 'function') {
            node.draw();
        } else {
            ctx.restore();
            return false;
        }
        ctx.restore();
        return true;
    }

    function refreshImages(){
        if( !stageRef.current){
            return
        }
        if(myState.current.timeoutPending){
            return
        }
        if(myState.current.animFramePending){
            return
        }
        if( !myState.current.rescaleList  ){
            return
        }
        const delay = 1 +(Math.random() * 5)

        if(!myState.current.width || !myState.current.height){
            setTimeout(() => {
                refreshImages()
            }, delay)
            return
        }
        
        myState.current.timeoutPending = setTimeout(() => {
            myState.current.timeoutPending = undefined
            myState.current.animFramePending = requestAnimationFrame(()=>{
                myState.current.animFramePending = undefined
                let steps = 200
                let toProcess = myState.current.rescaleList.splice(0,steps)
                const overlapBatch = new Set()
                for(let idx = 0; idx < steps; idx++){
                    const d = toProcess[idx]
                    if( d ){
                        if( !d.parent?.attrs.removing && d.getLayer()){
                            d.refreshCache()
                            const nodesToRefresh = collectNodesRenderedAfter(d)
                            let clippedDraw = false
                            if( useOverlapClipMask && nodesToRefresh.size > 0){
                                clippedDraw = drawNodeWithOverlapMask(d, nodesToRefresh)
                            }
                            if( !clippedDraw ){
                                d.draw()
                            }
                            const shouldQueueOverlap = nodesToRefresh.size > 0 && (!useOverlapClipMask || !clippedDraw)
                            if( shouldQueueOverlap ){
                                nodesToRefresh.forEach((node)=>overlapBatch.add(node))
                            }
                        }else{
                            d.resetOwner()
                        }
                    }
                }
                if( overlapBatch.size > 0){
                    scheduleOrderingRefresh(overlapBatch)
                }
                if( myState.current.rescaleList.length > 0){
                    refreshImages()
                }
            })
        }, delay);
            
    }

    function processImageCallback(image, parent){
        if( !myState.current.rescaleList ){
            myState.current.rescaleList = []
        }
        myState.current.rescaleList.push( image )
        refreshImages()
    }
    function buildPositionCache(frame){
        if(!stageRef.current){
            return
        }        
        if( !enablePages ){
            return
        }
        
        if(frame.pages){
            //throw `Pages already present for frame ${frame.id}`
            frame.pages = []
        }else{
            frame.pages = []
        }


        let nodes = frame.allNodes ?? frame.node.children
        for(const d of nodes){

            let l = d.attrs.overrideX !== undefined ? d.attrs.overrideX : d.x()
            let t = d.y()
            let r = l + d.width() 
            let b = t + d.height() 
            let page

            if( frame.scale !== 1){
                l *= frame.scale
                t *= frame.scale
                r *= frame.scale
                b *= frame.scale
            }

            if( enablePages){
                let [px,py] = calcPage( l, t)
                let [mx,my] = calcPage( r, b)

                for(let j = py; j <= my; j++){
                    for(let i = px; i <= mx; i++){
                        frame.pages[i] ||= []
                        frame.pages[i][j] ||= []

                        frame.pages[i][j].push(d)
                    }
                }
                d.attrs['page'] = page
            }
        }
        const [maxCol,maxRow] = calcPage( frame.node.width() * frame.scale, frame.node.height() * frame.scale)
        frame.maxCol = maxCol
        frame.maxRow  = maxRow
    }
    function getFrame(id){
        return myState.current?.frames?.find(d=>d.id === id)
    }
    function updateIndicators(id, indicatorData){
        const frame = myState.current.frames.find(d=>d.id === id)
        if( !frame ){
            return
        }

        if( !frame.label ){
            return
        }

        if( indicatorData ){
            frame.label.indicatorData = { ...indicatorData }
        }else{
            frame.label.indicatorData = undefined
        }
        const stageScale = stageRef.current?.scale()?.x ?? 1
        const frameScale = frame.scale ?? 1
        const scaleFactor = frame.labelScale ?? Math.min(25, Math.max(1 / stageScale / frameScale, 2))
        layoutFrameLabel(frame, scaleFactor)
        const layer = frame.label.getLayer()
        if( layer ){
            layer.batchDraw()
        }

    }
    function updateActiveChip(id, active){
        const frame = myState.current.frames.find(d=>d.id === id)
        if( !frame || !frame.label ){
            return
        }
        const wasActive = !!frame.label.activeChip
        if( active && !wasActive ){
            const chip = createRunningPulseChip()
            frame.label.add(chip)
            frame.label.activeChip = chip
        }else if( !active && wasActive ){
            frame.label.activeChip.destroy()
            frame.label.activeChip = undefined
        }else{
            return
        }
        // force re-layout so chip slots correctly beside title
        frame.label._lastFrameWidth = undefined
        const stageScale = stageRef.current?.scale()?.x ?? 1
        const frameScale = frame.scale ?? 1
        const scaleFactor = frame.labelScale ?? Math.min(25, Math.max(1 / stageScale / frameScale, 2))
        layoutFrameLabel(frame, scaleFactor)
        const layer = frame.label.getLayer()
        if( layer ){
            layer.batchDraw()
        }
    }

    function createFrame(options = {}){
        const target = stageRef.current?.children[0]
        if( !target ){
            return
        }
        let frameId = myState.current?.frames?.length ?? 0
        const frame = new Konva.Group({
            x: options.x ?? 0,
            y: options.y ?? 0,
            scaleX: options.s ?? 1,
            scaleY: options.s ?? 1,
            name: "frame",
            id: options.id ??  `f${frameId}`
        }) 
        target.add(frame)

        let frameOffset = 0
        let ring

        if( options.ringOffset && options.halfRingWidth ){
            ring = new Konva.Rect({
                x: options.halfRingWidth,
                y: options.halfRingWidth,
                width: 100,
                height: 100,
                ringColor: mixHex( options.frameColor, myState.current.background, 0.7),
                stroke: myState.current.background,
                strokeScaleEnabled: true,
                cornerRadius: options.halfRingWidth * 2,
                strokeWidth: (options.halfRingWidth * 2) - 1,
                fill: myState.current.background,
                "name":"ring"
            })
            frameOffset = options.ringOffset
            frame.add(ring)
        }
        const frameBg = new Konva.Rect({
            x: frameOffset,
            y: frameOffset,
            width: 1000,
            height: 10,
            fill: options.frameless ? "transparent" : options.bgFill ?? "#fafafa",
            cornerRadius: options.flow ? 50 : options.frameless ? undefined : 10,
            strokeScaleEnabled: false,
            visible: props.board,
            stroke: undefined,
            name:"frame_bg",
            id: `frame`,
        }) 
        frame.add(frameBg)
        const frameBorder = new Konva.Rect({
            x: frameOffset,
            y: frameOffset,
            width: 1000,
            height: 10,
            fill:undefined,//"#fcfcfc",
            dash: options.flow ? [8, 4] : undefined,
            //cornerRadius: 10,
            cornerRadius: options.flow ? 50 : options.frameless ? undefined : 10,
            strokeScaleEnabled: !options.flow,
            visible: props.board,
            strokeWidth: options.flow ? 1.5 : 3,
            stroke: options.frameColor ?? (options.flow ? "#a0a0a0" : options.frameless ? "transparent" : "#d2d3cf"),
            name:"frame_outline",
            id: `frame`,
        }) 
        frame.add(frameBg)
        frame.add(frameBorder)
        
        
        
        myState.current.frames ||= []

        
        myState.current.frames[frameId] = {
            id: options.id ?? frameId,
            node: frame,
            border: frameBorder,
            ring,
            ringOffset: options.ringOffset,
            halfRingWidth: options.halfRingWidth,
            bg: frameBg,
            x: options.x ?? 0,
            y: options.y ?? 0,
            scale: options.s ?? 1
        }
        
        return myState.current.frames[frameId]
    }
    function processAnimationQueue(){
        if( myState.current.animationRequest ){
            console.log(`defer anim request`)
        }
        if( myState.current.animationQueue?.length > 0){
            myState.current.animationRequest = requestAnimationFrame((tick)=>{
                let l, c
                for(const d of myState.current.animationQueue){
                    if(d.node.attrs._vis !== myState.current.refreshCount){
                        continue
                    }
                    l = l ?? d.node.getLayer()
                    c = c ?? (l && l.getCanvas())
                    

                    if( c && c.width > 0 && c.height > 0  ){
                        if(d.callback){
                            if(d.callback(tick)){
                                d.node.draw()
                            }
                        }
                    }
                }
                myState.current.animationRequest = undefined
                if( myState.current.animationQueue?.length > 0){
                    processAnimationQueue()
                }
            })
        }
    }
    function animationCallback(g, callback, options){
        myState.current.animationQueue = myState.current.animationQueue || []
        myState.current.animationQueue.push( {node: g, callback, options} )
        processAnimationQueue()
    }
    function removeFromAnimationQueue( animationNodeId ){
        if(myState.current.animationQueue ){
            const old = myState.current.animationQueue.length
            myState.current.animationQueue = myState.current.animationQueue.filter(d=>d.node._id !== animationNodeId )
            console.log(`REMOVED ${old-myState.current.animationQueue.length} nodes`)
        }

    }
    function updateIndicatorPosition( id, frame){
        frame ||= myState.current.frames.find(d=>d.id === id)
        if( !frame?.label ){
            return
        }
        const scaleFactor = frame.labelScale ?? 1
        layoutFrameLabel(frame, scaleFactor)

    }

    function resizeNestedFrame( id ){
        const frame = myState.current.frames.find(d=>d.id === id)
        const children = getNestedFrames(id).map(d=>{
            const frame = myState.current.frames.find(d2=>d.id === d2.id)
            if( frame ){
                return {
                    id: d.id,
                    frame,
                    node: frame.node
                }
            }
        }).filter(d=>d)
        const positions = children.map(d=>{
            const rp = getRelativeFramePosition( d.id)
            const nodeWidth = d.node.width() 
            const nodeHeight = d.node.height() 
            const w = nodeWidth * rp.s
            const h = nodeHeight * rp.s
            return {
                id: d.id,
                frame: d.frame,
                x: rp.x,
                y: rp.y,
                w,
                h,
                b: rp.y + h,
                r: rp.x + w,
                nodeWidth,
                nodeHeight
            }
        })

        if( !frame?.resizeForChildren){
            const fw = frame.node.width() - 1
            const fh = frame.node.height() - 1
            for(const child of positions){
                const s = frame.node.scaleX() / child.frame.node.scaleX() 
                
                let vw = child.r > fw ? Math.max((fw - child.x) * s,0) : undefined
                let vh = child.b > fh ? Math.max((fh - child.y) * s,0) : undefined
                let x = 0
                let y = 0
                if( child.x < 1){
                    x = (1 - child.x) * s
                    vw = (vw ?? child.nodeWidth) - x
                }
                if( child.y < 1){
                    y = (1 -child.y) * s
                    vh = (vh ?? child.nodeHeight) - y
                }
                
                if( vw === undefined && vh ===undefined ){
                    child.frame.node.clipWidth(undefined)
                    child.frame.node.clipHeight(undefined)
                }else{
                    child.frame.node.clip({
                        x,
                        y,
                        width: vw ?? child.nodeWidth,
                        height: vh ?? child.nodeHeight
                    })
                }

            }


            orderNestedFrames(frame.node)
            return
        }
        
        if( positions.length > 0){

            const maxX = Math.max(...positions.map(d=>d.r),0) + 60
            const maxY = Math.max(...positions.map(d=>d.b),0) + 60
            
            if( frame?.node ){
                const frameBorder = frame.border
                if( frame.bg){
                    frame.bg.width(maxX)
                    frame.bg.height(maxY)
                }
                if( frameBorder){
                    frameBorder.remove()
                    frameBorder.width(maxX)
                    frameBorder.height(maxY)
                    frame.node.add(frameBorder)
                }
                frame.node.width(maxX)
                frame.node.height(maxY)

                updateIndicatorPosition( id, frame)

                if( myState.current.frameSelect ){
                    if( myState.current.frameSelect.node.attrs.id === id ){
                        clearSelection()                    
                        destroyFrameSelect()
                        createFrameSelect( frame.node )
                    }
                }
            }
            setupPins( frame )
            removeRoutingForFrame(frame)
            addRoutingForFrame(frame)
            buildPositionCache(frame)
        }
        if( frame.parentRender ){
            resizeNestedFrame( frame.parentRender )
        }
        

    }
    function setupPins( frame){
        if( props.showPins === false){
            return
        }
        const ringOffset = frame.ringOffset ?? 0
        let maxX = frame.node.attrs.width - ringOffset
        let maxY = frame.node.attrs.height - ringOffset
        const spacingY = 16
        const size = 8
        const midY = maxY / 2
        const inputPinStart = midY - ((1 + Math.round(Math.max(...frame.pins.input.map(d=>d.rIdx)) / 2)) * spacingY)
        const outputPinStart = midY - ((1 + Math.round(Math.max(...frame.pins.output.map(d=>d.rIdx)) / 2)) * spacingY)
        frame.pins.input = frame.pins.input.map(d=>({
            ...d,
            output: false,
            data: {name: d.name, output: false},
            dir: 4,
            x: d.internal ? (maxX - 1)  : ringOffset + 1,
            y: inputPinStart + (d.rIdx * spacingY) 
        }))
        frame.pins.output = frame.pins.output.map(d=>({
                ...d,
                output: true,
                data: {name: d.name, output: true},
                dir: 8,
                x: d.internal ? ringOffset : maxX,
                //x: d.internal ? (1/maxX) : ((maxX - 1) / maxX) ,
                y: outputPinStart + (d.rIdx * spacingY)
        }))
        
        {
            [...frame.pins.input, ...frame.pins.output].forEach(d=>{
                let n = d.node
                if( !n ){
                    n = new Konva.Rect({
                        width: size / 2,
                        height: size,
                        name:'pin top_layer',
                        data: d.data,
                        fill: d.rIdx === 0 ? '#333' : '#999',
                        hoverFill: "red",
                        minRenderSize: 3,
                        id: `${frame.id}_${d.idx}`
                    }) 
                    d.node = n
                }
                n.attrs.expandedClick = true
                frame.node.add(n)
                n.position({x: d.x - (d.output ? 0 : size / 2 ), y: d.y - (size / 2)})

                if( d.showLabel !== false){

                    let t = d.textNode
                    if( !t ){
                        t = new CustomText({
                            text: d.label ?? d.name,
                            name:'top_layer pin_label',
                            width: "auto",
                            fontSize: 8,
                            minRenderSize: 20,
                        })
                        d.textNode = t
                    }
                    frame.node.add(t)
                    t.position({
                        x: (d.output && !d.internal) ? maxX + (ringOffset / 2) + (size * 0.7) : n.x() -t.width() - (size * 0.7),
                        y: d.y - 4
                    })
                    if(!d.output && !d.internal){
                        t.attrs.overrideX = 0
                    }
                }
            })
        }

    }

    function layoutFrameLabel(frame, scaleFactor = 1){
        if( !frame?.label ){
            return
        }

        const label = frame.label
        const titleNode = label.titleNode
        if( !titleNode ){
            return
        }

        const frameWidth = frame.node.width()
        const indicatorKey = label.indicatorData ? `${label.indicatorData.count}|${label.indicatorData.progress ?? ''}` : 'none'
        if( label.labelScale === scaleFactor && label._lastIndicatorKey === indicatorKey && label._lastFrameWidth === frameWidth ){
            return
        }

        const originWidth = label.attrs.originWidth ?? titleNode.width()
        const labelBg = label.labelBg

        const baseFontSize = label.attrs.scaleFont ?? titleNode.fontSize() ?? 12
        const fontSize = baseFontSize * scaleFactor
        if( titleNode.fontSize() !== fontSize ){
            titleNode.fontSize(fontSize)
        }

        const titleHeight = fontSize//titleNode.height() || fontSize
        const labelOffsetY = titleHeight + Math.max(2, 2 * scaleFactor)

        if( label.offsetY() !== labelOffsetY ){
            label.offsetY(labelOffsetY)
        }
        if( label.height() !== titleHeight ){
            label.height(titleHeight)
        }
        if( labelBg ){
            if( labelBg.height() !== titleHeight ){
                labelBg.height(titleHeight)
            }
        }

        let indicatorWidth = 0
        let indicatorHeight = 0
        let outerGap = label.indicatorBase?.outerGap ?? 10

        if( label.indicatorData ){
            const baseBarWidth = label.indicatorBase?.barWidth ?? 60
            const baseInsideGap = label.indicatorBase?.insideGap ?? 6
            const baseOuterGap = label.indicatorBase?.outerGap ?? 10
            const baseFontSizeIndicator = label.indicatorBase?.countFontSize ?? 12

            const indicator = renderIndicators(label.indicatorData, {
                label,
                baseBarWidth,
                baseInsideGap,
                baseOuterGap,
                baseFontSize: baseFontSizeIndicator,
                scaleFactor: scaleFactor ** 0.5,
                titleHeight
            })

            if( indicator ){
                indicatorWidth = indicator.width()
                outerGap = baseOuterGap
                const indicatorX = Math.max(0, frameWidth - indicatorWidth)
                indicator.height(titleHeight)
                indicatorHeight = titleHeight
                const indicatorY = Math.max(0, (titleHeight - indicatorHeight) / 2) - (labelOffsetY / 2)
                if( indicator.x() !== indicatorX ){
                    indicator.x(indicatorX)
                }
                if( indicator.y() !== indicatorY ){
                    indicator.y(indicatorY)
                }
            }
        }else{
            renderIndicators(undefined, {label})
        }

        const indicatorPresent = !!label.indicatorGroup
        const maxLabelWidthWithoutIndicator = Math.min(originWidth * scaleFactor, frameWidth * 1.25)
        const labelWidth = indicatorPresent ? frameWidth : maxLabelWidthWithoutIndicator

        let availableTitleWidth = Math.min(originWidth * scaleFactor, labelWidth)
        if( indicatorPresent ){
            availableTitleWidth = Math.max(0, labelWidth - indicatorWidth - outerGap)
            availableTitleWidth = Math.min(availableTitleWidth, originWidth * scaleFactor)
        }

        if( titleNode.width() !== availableTitleWidth ){
            titleNode.width(availableTitleWidth)
        }
        if( titleNode.height() !== titleHeight ){
            titleNode.height(titleHeight)
        }
        if( titleNode.y() !== 0 ){
            titleNode.y(0)
        }

        if( label.activeChip ){
            // place the pulse chip just to the right of the title text
            const chipScale = Math.max(1, scaleFactor)
            label.activeChip.scale({x: chipScale, y: chipScale})
            const titleEnd = titleNode.x() + Math.min(titleNode.getTextWidth(), availableTitleWidth)
            const chipX = titleEnd + Math.max(4, 4 * scaleFactor)
            const chipY = (titleHeight - (label.activeChip.height() * chipScale)) / 2
            label.activeChip.x(chipX)
            label.activeChip.y(chipY)
            label.activeChip.moveToTop()
        }

        if( label.width() !== labelWidth ){
            label.width(labelWidth)
        }
        if( labelBg && labelBg.width() !== labelWidth ){
            labelBg.width(labelWidth)
        }

        frame.indicators = label.indicatorGroup ?? null
        label.labelScale = scaleFactor
        frame.labelScale = scaleFactor
        label._lastIndicatorKey = indicatorKey
        label._lastFrameWidth = frameWidth
    }

    function setupFrameForItems( id, title, items, x, y, s, options ){
        let ids, removeIds
        const existing = myState.current.frames?.find(d=>d.id === id) 
        let existingNode
        if(existing){
            existing.node.children = existing.allNodes
            existingNode = existing.node
            removeRoutingForFrame( existing )
            myState.current.frames = myState.current.frames.filter(d=>d.id !== id) 
        }
        let framePadding = options.canvasMargin ?? [5,5,5,5]
        let ringOffset = 0, halfRingWidth = 0
        if( !options.frameless && !options.ringless){
            ringOffset = 8
            halfRingWidth = 5
        }
        
        const frame = createFrame({id: id, x, y, s, bgFill: options.bgFill, frameColor: options.frameColor, frameless: options.frameless, flow: options.flow, ringOffset, halfRingWidth})
        if( frame ){
            const frameBorder = frame.border//node.find('#frame')?.[0]
            
            if( props.board ){
                if( !options.frameless && title && !props.singleFrame ){

                    const label = new Konva.Group({
                        x:0,
                        y:0,
                        offsetY:18,
                        width:100,
                        height:18,
                        name:"frame_label",
                    })
                    const labelBg = new Konva.Rect({
                        x:0,
                        y:0,
                        width:18,
                        height:18,
                        fill: myState.current.background ?? "#ffffff"
                    })
                    label.add(labelBg)
                    const titleText = new Konva.Text({
                        text: typeof(title) == "function" ? title() : title,
                        x:0,
                        y: 0,
                        verticalAlign:"middle",
                        fontSize:12,
                        lineHeight: 1.5,
                        height: 12 - 1.5,
                        color: '#444',
                        wrap: false,
                        ellipsis: true
                    })
                    const titleWidth = titleText.width()
                    const titleHeight = titleText.height()
                    label.offsetY(titleHeight)
                    label.width(titleWidth)
                    label.height(titleHeight)
                    labelBg.width(titleWidth)
                    labelBg.height(titleHeight)
                    label.attrs.originWidth = titleWidth
                    label.attrs.scaleFont = 12
                    label.attrs.hideScale = options.titleAlwaysPresent ?  undefined : 3
                    label.attrs.baseOffsetY = label.offsetY()
                    label.attrs.baseHeight = titleHeight
                    

                    
                    label.add(titleText)
                    label.labelBg = labelBg
                    label.titleNode = titleText
                    frame.label = label
                    frame.node.add(label)
                }
            }

            const rendered = items({imageCallback: processImageCallback, amimCallback: animationCallback, x: framePadding[3] + ringOffset, y: framePadding[0] + ringOffset, utils: options.utils})
            if( rendered?.attrs?.abortFrame ){
                removeRoutingForFrame( frame )
                if( frame?.node ){
                    frame.node.destroy()
                }
                myState.current.frames = myState.current.frames.filter(d=>d.id !== id)
                return null
            }
            framePadding = rendered?.attrs?.canvasMargin ?? framePadding

            const root = convertItems( rendered, frame.node)
            ids = frame.node.find('.primitive').map(d=>d.attrs.id)
            frame.cells = frame.node.find('.cell').map(d=>({id: d.attrs.id, l: d.attrs.x, t: d.attrs.y, r: (d.attrs.x + d.attrs.width), b: (d.attrs.y + d.attrs.height)}))
            const maxX = root.width() + framePadding[1] + framePadding[3]
            const maxY = root.height() + framePadding[0] + framePadding[2]
            
            frame.allNodes = frame.node.children
            frame.canChangeSize = options.canChangeSize
            frame.canvasMargin = framePadding
            frame.resizeForChildren = options.resizeForChildren
            frame.routeInternal = options.routeInternal
            frame.isContainer = options.isContainer
            
            
            if( frame.ring){
                frame.ring.width(maxX + (ringOffset * 2) - (halfRingWidth * 2))
                frame.ring.height(maxY+ (ringOffset * 2) - (halfRingWidth * 2))
            }
            if( frame.bg){
                frame.bg.position({x: ringOffset, y: ringOffset})
                frame.bg.width(maxX)
                frame.bg.height(maxY)
            }
            if( frameBorder){
                frameBorder.position({x: ringOffset, y: ringOffset})
                frameBorder.remove()
                frameBorder.width(maxX)
                frameBorder.height(maxY)
                frame.node.add(frameBorder)
            }
            frame.node.width(maxX + (ringOffset * 2))
            frame.node.height(maxY + (ringOffset * 2))
            if( options.pins ){
                frame.pins = options.pins 
                setupPins(frame)
            }

            if( options.indicators ){
                const indicatorData = options.indicators()
                renderIndicators(indicatorData, {
                    label: frame.label
                })
                frame.indicators = frame.label?.indicatorGroup
            }
            if( options.activeChip && frame.label ){
                const active = typeof options.activeChip === "function" ? options.activeChip() : options.activeChip
                if( active && !frame.label.activeChip ){
                    const chip = createRunningPulseChip()
                    frame.label.add(chip)
                    frame.label.activeChip = chip
                }
            }
            layoutFrameLabel(frame, 1)
            frame.routing = existing?.routing
            if( addRoutingForFrame( frame ) ){
                refreshLinks()
            }
            

            if( options.parentRender ){
                frame.parentRender = options.parentRender
                resizeNestedFrame( options.parentRender )
            }
        }
        if( existingNode ){
            if( frame ){
                const newItems = frame.node.find('.inf_keep')
                let existingItems = options.forceRender ?[] : existingNode.find('.inf_keep')
                for(const d of newItems){
                    if( !d.attrs.id ){continue}
                    const match = existingItems.filter(d2=>d.attrs.id === d2.attrs.id )
                    if( match.length > 0){
                        if( match.length === 1){
                            const parent = d.parent
                            match[0].remove()
                            match[0].setAttrs(d.attrs)
                            match[0].attrs["_txc"] = undefined
                            match[0].attrs._vis = undefined
                            if( match[0].resetOwner ){
                                match[0].resetOwner()
                            }

                            parent.add(match[0])
                            d.destroy()

                            existingItems = existingItems.filter(d2=>d.attrs.id !== d2.attrs.id)
                        }
                    }
                }
                
            }

            //console.log(`Found existing frame - removing`)
            existingNode.children.forEach(d=>{
                if( d.attrs.hasAnimationNode ){
                    console.log(`will remove from animation queue`)
                    removeFromAnimationQueue(d.attrs.hasAnimationNode)
                }
                d.attrs.removing = true
            })
            existingNode.remove()
            stageRef.current.batchDraw()
        }

        if( props.updateWatchList ){
            props.updateWatchList(frame.id, ids)
        }

        return frame
    }
    function restoreChildren(node, offsetSource, list){
        if( !list ){
            list = offsetSource.find(d=>d)
        }

        const children = list.filter(d=>d.original?.parent?._id === node._id)
        for(const child of children){
            child.attrs.cachedParent = child.parent
            child.ox = child.x()
            child.oy = child.y()
            child.os = child.scaleX()
            child.remove()
            //child.x( (child.ox / pScale) - child.attrs.originalParent.x() )
            //child.y( (child.oy / pScale) - child.attrs.originalParent.y() )
            //child.scale({x: newScale, y: newScale})
            child.position( {x: child.original.x, y: child.original.y} )
            child.scale({x: child.original.s, y: child.original.s})
            child.original.parent.add( child )
            restoreChildren(child, offsetSource, list)
        }
    }
    function restoreChildrenForTracking(node){
        for(const child of node.children){
            if( child.attrs.cachedParent ){                
                child.remove()
                child.position( {x: child.ox, y:child.oy })
                child.scale({x: child.os, y: child.os})
                child.attrs.cachedParent.add( child )
                delete child.attrs["cachedParent"]
                delete child["ox"]
                delete child["oy"]
                restoreChildrenForTracking(child)
            }
        }
    }

    function convertItems(g, frame, padding = 0){
        if(  typeof(g) === "object" ){

            const moveList = []
            g.find('.inf_track').forEach(d=>{
                let x = d.x(), y = d.y(), s = d.scaleX()
                let cl, ct, cw, ch

                let childClip = d.clip?.();
                if (!childClip || childClip.x === undefined) {
                   childClip = { x: 0, y: 0, width: d.width(), height: d.height() };
                }

                for(const p of d.findAncestors('Group')){
                    const ps = p.scaleX()
                    x = (x * ps) + p.x()
                    y = (y * ps) + p.y()
                    s *= ps
                    const pClip = p.clip?.();
                    if (!pClip || pClip.x === undefined) continue;

                    // Map parent's clip rect -> CHILD LOCAL space
                    // toChild = (d_abs)^-1 * (p_abs)
                    const toChild = d.getAbsoluteTransform().copy().invert().multiply(p.getAbsoluteTransform());

                    const corners = [
                        { x: pClip.x,                 y: pClip.y },
                        { x: pClip.x + pClip.width,   y: pClip.y },
                        { x: pClip.x + pClip.width,   y: pClip.y + pClip.height },
                        { x: pClip.x,                 y: pClip.y + pClip.height },
                    ].map(pt => toChild.point(pt));

                    const xs = corners.map(pt => pt.x);
                    const ys = corners.map(pt => pt.y);
                    const pcLocal = {
                        x: Math.min(...xs),
                        y: Math.min(...ys),
                        width:  Math.max(0, Math.max(...xs) - Math.min(...xs)),
                        height: Math.max(0, Math.max(...ys) - Math.min(...ys)),
                    };

                    // Intersect with current child-local clip
                    const ix = Math.max(childClip.x, pcLocal.x);
                    const iy = Math.max(childClip.y, pcLocal.y);
                    const ir = Math.min(childClip.x + childClip.width,  pcLocal.x + pcLocal.width);
                    const ib = Math.min(childClip.y + childClip.height, pcLocal.y + pcLocal.height);

                    childClip = {
                        x: ix,
                        y: iy,
                        width:  Math.max(0, ir - ix),
                        height: Math.max(0, ib - iy),
                    };
                }
                cl = childClip.x;
                ct = childClip.y;
                cw = childClip.width;
                ch = childClip.height;

                d.original = {
                    parent: d.parent,
                    //x: d.x(),
                    //y: d.y(),
                    //s: d.scaleX(),
                    x: d.attrs.x,
                    y: d.attrs.y,
                    s: d.attrs.scaleX
                }

                d.remove()
                d.x(x )
                d.y(y )
                d.scale({x: s, y: s})
                if( cl !== undefined && d.clip){
                    d.clip({
                        x: cl,
                        y: ct,
                        width: cw,
                        height: ch
                    })                    
                }
                moveList.push(d)

            })                    
            frame.add(g)
            moveList.forEach(d=>frame.add(d))
        }else{
            let maxX = 0, maxY = 0
            test_items.forEach((d,i)=>{
                g = new Konva.Group({
                    id: `g_${i}`,
                    x: d.x * 80,
                    y: d.y * 80,
                    width: 80,
                    height: 80,
                    name:"primitive",
                    visible: enableNodePruning
                })
                
                let r = g.x() + g.width()
                let b = g.y() + g.height()
                if( r > maxX){ maxX = r }
                if( b > maxY){ maxY = b }

                if( g ){
                    frame.add(g)
                    const r = new Konva.Rect({
                        width: 72,
                        height: 72,
                        fill: '#fafafa',
                        stroke: '#888'
                    })
                    g.add(r)
                    const t = new Konva.Text({
                        x: 5,
                        y: 5, 
                        width: 50,
                        height: 50,
                        text: `${d.x} / ${d.y}`,
                        fontSize: 12,
                        fill: '#000'
                    })
                    g.add(t)
                }
            })            
        }
        return g
    }

    function resizeFrame(w, h){
        if( frameRef.current && stageRef.current){
            if( myState.current.resizeTimer ){
                clearTimeout( myState.current.resizeTimer )
            }
            myState.current.resizeTimer = setTimeout(() => {
                myState.current.width = w ?? frameRef.current.offsetWidth
                myState.current.height = h ?? frameRef.current.offsetHeight
                stageRef.current.width( (enableFlipping ? 2 : 1) * myState.current.width)
                stageRef.current.height( (enableFlipping ? 2 : 1) * myState.current.height)
                if( enableFlipping ){
                    let [x,y,scale] = restoreTransform()
                    alignViewport(x,y,scale)
                    updateVisibility(myState.current.flipping?.last.cx ?? 0, myState.current.flipping?.last.cy ?? 0)
                    stageRef.current.batchDraw()
                }
                let shouldZoomToExtents = myState.current.needExtentZoom
                if( props.singleFrame ){
                    const baseScale = myState.current.singleFrameInfo?.base?.scale
                    const currentScale = myState.current.viewport?.scale ?? baseScale ?? 1
                    if( baseScale === undefined || currentScale <= baseScale + SINGLE_FRAME_EPSILON ){
                        shouldZoomToExtents = true
                    }
                }
                if( shouldZoomToExtents ){
                    zoomToExtents()
                }
            }, 150);
            
        }
    }
    function refreshLinksDuringDrag( override ){
        if( myState.current.linkUpdatePending ){
            return
        }
        refreshLinks( override)
    }
    function computeDistance(frameId, current, param, distance){
        return myState.current.frames.reduce((a,d)=>{
            if( d.id === frameId){
                return a
            }
            let di = (d[param]) ? Math.abs(current - d[param]) : undefined
            let v = d[param]
            if( di){
                let v2 = v + (param === "x" ? d.node.width() * d.node.scaleX( ): d.node.height() * d.node.scaleY( ))
                const di2 = Math.abs(current - v2)
                if( di2 < di ){
                    di = di2
                    v = v2
                }

                if( di < distance){
                    if( di < a.d){
                        return {v, d: di}
                    }
                }
            }
            return a
        }, {v: undefined, d: Infinity})
    }

    function rescaleLinks(){
        let scale = myState.current.viewport?.scale ?? 1
        let pw = linkArrowSize / scale
        
        if( myState.current.frameLinks ){

            for(const d of myState.current.frameLinks){
                d.pointerLength( pw )
                d.pointerWidth( pw )
            }
        }

    }
    function addShapeToRouter(id, l,t,r,b, pins = [], obstacle = true){
        if( !myState.current.routing ){return}
        myState.current.routing.router.addShape({
            id,
            left: l,
            top: t,
            width: r-l,
            height: b-t,
            obstacle
        })
    }
    /*function removeRoutingForFrame( frame ){
    }*/
    /*function addShapeToRouter(id, l,t,r,b, pins = []){
        const Avoid = myState.current.routing.avoid
        let router = myState.current.routing.router
        const shapeRef = new Avoid.ShapeRef(router, 
            new Avoid.Rectangle(
                new Avoid.Point(l, t),
                new Avoid.Point(r, b)
        ));

        for(const d of pins){
            const pin = new Avoid.ShapeConnectionPin(
                shapeRef,
                d.idx,
                d.x,
                d.y,
                true,
                0,
                d.dir ?? Avoid.ConnDirLeft
            );
            pin.setExclusive(false);
            pin.setExclusive(false);
        }
            
        return shapeRef
    }*/
    function removeRoutingForFrame( frame ){
        if( frame.routing){
            if( myState.current.routing?.routerLinks){
                myState.current.routing.routerLinks = myState.current.routing.routerLinks.filter(d=>{
                    let [leftFull,rightFull] = d.id.split("~")
                    let [left, lPin] = leftFull.split(".")
                    let [right, rPin] = rightFull.split(".")
                    if( left === frame.id || right === frame.id){
                        myState.current.routing.router.removeLink( d.id)
                        return false
                    }
                    return true
                })
            }
            if(myState.current.routing?.router){
                myState.current.routing.router.removeShape({id: frame.id})
            }
            delete frame["routing"]
        }
    }

    function addRoutingForFrame( frame ){
        if( !myState.current.routing ){return}
        const fId = frame.id
        let position = framePosition(fId)?.scene

        const pins = []

        if( frame.pins ){

            if( frame.pins.input ){
                for(const d of frame.pins.input ){
                    pins.push({
                        ...d,
                        x: (d.internal ? (position.width - 2) : d.x) / position.width,
                        y: d.y / position.height,
                    })
                }
            }else{
                pins.push({
                    idx: 1,
                    x: 0,
                    y: 0.5,
                })
            }
            if( frame.pins.output ){
                for(const d of frame.pins.output ){
                    pins.push({
                        ...d,
                        x: (d.internal ? 1 : d.x) / position.width,
                        y: d.y / position.height,
                    })
                }
            }else{
                pins.push({
                    idx: 1,
                    x: 0,
                    y: 0.5
                })
            }
        }
        const o = frame.ringOffset
        frame.routing = {
            [fId]: {
                shape: addShapeToRouter( fId, position.l + o, position.t + o, position.r - o, position.b - o, pins, !frame.routeInternal)
            }
        }

        if( myState.current.baseLinks ){
            /*for(const cell of frame.cells){
                if( myState.current.baseLinks.find(d=>d.left === fId && d.cell === cell.id) ){
                    const id = `${fId}:${cell.id}`
                    frame.routing[id] = {
                        shape: addShapeToRouter( id, (cell.l * position.s) + position.l , (cell.t  * position.s) + position.t, (cell.r  * position.s) + position.l , (cell.b * position.s) + position.t),
                        cell: cell
                    }
                }
            }*/
        }
        return true
    }
    function getLinks( links ){
        return myState.current.baseLinks
    }
    function updateFramePosition( frameId, details){
        myState.current.framePositions ||= {}
        myState.current.framePositions[frameId] = {
            ...myState.current.framePositions[frameId],
            ...details
        }


    }
    function updateLinks( links ){
        console.log(`UPDATING LINKS IN IC`)
        myState.current.baseLinks = links

        const frameList = new Set()
        
        for(const d of myState.current.baseLinks){
            frameList.add( d.left)
            frameList.add( d.right)
        }
        for(const d of frameList.values()){
            const frame = myState.current.frames.find(d2=>d2.id === d)
            if( frame ){
                removeRoutingForFrame(frame)
                addRoutingForFrame(frame)
            }
        }
        myState.current.routing.didLinks = false

        refreshLinks(false, true)
    }


    async function refreshLinks( override, force ){
        if( force && myState.current.linkUpdatePending){
            cancelAnimationFrame( myState.current.linkUpdatePending )
            myState.current.linkUpdatePending = undefined
        }
        if( myState.current.linkUpdatePending ){
            return
        }
        
        myState.current.linkUpdatePending = requestAnimationFrame(()=>{
            myState.current.linkUpdatePending = undefined            
            if( myState.current.baseLinks){
                if( !myState.current.frameLinks ){
                    myState.current.frameLinks = []                
                }
                if( !myState.current.routing){
                    myState.current.routing = {
                        routerLinks: [],
                        router: new OrthogonalConnector({
                            shapes:[],
                            shapeMargin: 10,
                            scale: myState.current.viewport?.scale ?? 1,
                            globalBoundsMargin: 10,
                            globalBounds: {left: 0, top: 0, width: 1500, height: 1500},
                            renderCallback:(paths)=>{
                                for(const path of paths ){
                                    renderLink(path)
                                }
                            }
                        })
                    }
                }

                // Define the graph with fixed node positions and edges
                
                for(const frame of myState.current.frames){
                    if( !frame.routing){
                        addRoutingForFrame(frame)
                    }
                }


                let activeLinks = new Set()

                if( myState.current.dragging?.sourcePin ){
                    activeLinks.add("pin_drag")
                }

                for(const target of myState.current.baseLinks){
                    const leftName = target.left + (target.cell ? `:${target.cell}` : "")
                    const left =  {id: target.left}
                    const right = {id: target.right}

                    const leftShape = myState.current.frames.find(d=>d.id === left.id)
                    const rightShape = myState.current.frames.find(d=>d.id === right.id)

                    
                    if( left && right && leftShape && rightShape ){
                        let leftPin = target.leftPin ?? 1
                        let rightPin = target.rightPin ?? 1
                        const id = `${leftName}.${leftPin}~${target.right}.${rightPin}` 

                        const leftPinDef = Object.values(leftShape.pins).flat().find(d=>d.idx === leftPin)
                        const rightPinDef = Object.values(rightShape.pins).flat().find(d=>d.idx === rightPin)

                        if( !myState.current.routing.routerLinks.find(d=>d.id===id)){
                            const link = {
                                id: id,
                                pointA: {
                                    shape: left,
                                    side: leftPinDef.internal ? "left" : "right",
                                    distance: leftPinDef.y / leftShape.node.height()
                                },
                                pointB: {
                                    shape: right,
                                    side: rightPinDef.internal ? "right" : "left",
                                    distance: rightPinDef.y / rightShape.node.height()
                                }
                            }
                            if( myState.current.routing.didLinks ){
                                myState.current.routing.router.addLink( link )
                                myState.current.routing.routerLinks.push(link)
                            }else{
                                myState.current.routing.routerLinks.push(link)
                            }
                        }
                        activeLinks.add(id)
                    }
                }
                myState.current.routing.router.route(myState.current.routing.didLinks ? undefined : myState.current.routing.routerLinks)
                myState.current.routing.didLinks = true

                myState.current.frameLinks = myState.current.frameLinks.filter(d=>{
                    if( !activeLinks.has(d.attrs.id)){
                        d.remove()
                        return false
                    }
                    return true
                })

                if( myState.current.routing?.routerLinks ){
                    const routerLinks = myState.current.routing.routerLinks;
                    const remainingLinks = [];
                    for( const link of routerLinks ){
                        if( activeLinks.has(link.id) ){
                            remainingLinks.push(link);
                        }else if( link.id && myState.current.routing.router ){
                            myState.current.routing.router.removeLink( link.id );
                        }
                    }
                    myState.current.routing.routerLinks = remainingLinks;
                }



                if( override ){
                    for(const d of override){
                        updateFrameInRouter(d)
                    }
                }
            }
        })
    }
    /*async function refreshLinks( override ){
        
        if( myState.current.baseLinks){
            if( !myState.current.frameLinks ){
                myState.current.frameLinks = []                
            }
            if( !myState.current.avoid_loaded ){
                myState.current.avoid_loaded = true
                AvoidLib.load('/images/libavoid.wasm').then(data=>{
                    const Avoid = AvoidLib.getInstance();
                    const router = new Avoid.Router(Avoid.OrthogonalRouting)
                    //router.setRoutingParameter(Avoid.shapeBufferDistance, 8);
                    //router.setRoutingParameter(Avoid.idealNudgingDistance, 6);
                    router.setRoutingOption(Avoid.performUnifyingNudgingPreprocessingStep, false);
                    router.setRoutingOption(Avoid.improveHyperedgeRoutesMovingJunctions, false);
                    //router.setRoutingOption(Avoid.nudgeSharedPathsWithCommonEndPoint, false);
                    
                    
                    myState.current.routing = {
                        avoid: Avoid,
                        router: router,
                        routerLinks: []
                    }
                    refreshLinks()
                })
                return
            }
            if( !myState.current.routing){return}
            const Avoid = myState.current.routing.avoid
            const router = myState.current.routing.router

            // Define the graph with fixed node positions and edges
            
            let nodes = {}

            for(const frame of myState.current.frames){
                if( !frame.routing ){
                    addRoutingForFrame(frame)
                }
                //nodes[frame.id] = frame.routing
                nodes = {
                    ...nodes,
                    ...frame.routing
                }
            }


            let activeLinks = new Set()

            for(const target of myState.current.baseLinks){
                const leftName = target.left + (target.cell ? `:${target.cell}` : "")
                const left = nodes[leftName]?.shape
                const right = nodes[target.right]?.shape
                
                if( left && right){
                    let leftPin = target.leftPin ?? 1
                    let rightPin = target.rightPin ?? 1
                    const id = `${leftName}.${leftPin}~${target.right}.${rightPin}` 

                    if( !myState.current.routing.routerLinks.find(d=>d.id===id)){
                        const leftConnEnd = new Avoid.ConnEnd(left, leftPin)
                        const rightConnEnd = new Avoid.ConnEnd(right, rightPin)
                        const connRef = new Avoid.ConnRef(myState.current.routing.router);
                        connRef.setSourceEndpoint(leftConnEnd);
                        connRef.setDestEndpoint(rightConnEnd);
                        connRef.setCallback( renderLink, connRef)
                        myState.current.routing.routerLinks.push({
                            id: id,
                            route: connRef
                        })
                    }
                    activeLinks.add(id)
                }
            }
            myState.current.frameLinks = myState.current.frameLinks.filter(d=>{
                if( !activeLinks.has(d.attrs.id)){
                    d.remove()
                    return false
                }
                return true
            })
            if( override ){
                for(const d of override){
                    updateFrameInRouter(d)
                }
            }


            router.processTransaction()
            lineLayerRef.current.batchDraw()
        }
    }*/
    function updateFrameInRouter(d){
        if( d.id === "pin" && !myState.current.dragging){
            return
        }
        let position = framePosition(d.id)?.scene ?? {l: 0, r:2, t: 0, b: 2}
        position.r = position.r - position.l + d.x
        position.b = position.b - position.t + d.y
        position.l = d.x
        position.t = d.y
        
       // const ovrFrame = d.id === "pin" ? {routing: {pin: {shape: myState.current.dragging.shapeForPin}}} : myState.current.frames.find(d2=>d2.id === d.id)
       
       const frame = myState.current.frames.find(d2=>d2.id === d.id)
       const o = frame?.ringOffset ??  0
       myState.current.routing.router.moveShape(d.id, {left: position.l + o, top: position.t + o, width: position.r - position.l - o, height: position.b - position.t - o})

       
    }
    /*function updateFrameInRouter(d){
        if( d.id === "pin" && !myState.current.dragging){
            return
        }
        const Avoid = myState.current.routing.avoid
        const router = myState.current.routing.router
        let position = framePosition(d.id)?.scene ?? {l: 0, r:2, t: 0, b: 2}
        position.r = position.r - position.l + d.x
        position.b = position.b - position.t + d.y
        position.l = d.x
        position.t = d.y
        
        const ovrFrame = d.id === "pin" ? {routing: {pin: {shape: myState.current.dragging.shapeForPin}}} : myState.current.frames.find(d2=>d2.id === d.id)
        
        for( const ri in ovrFrame.routing){
            const r = ovrFrame.routing[ri]
            if( r.cell ){
                const shapeRect = new Avoid.Rectangle(
                    new Avoid.Point((r.cell.l * position.s) + position.l, (r.cell.t * position.s) + position.t),
                    new Avoid.Point((r.cell.r * position.s) + position.l, (r.cell.b * position.s) + position.t)
                );
                router.moveShape(r.shape, shapeRect);
            }else{
                const shapeRect = new Avoid.Rectangle(
                    new Avoid.Point(position.l, position.t),
                    new Avoid.Point(position.r, position.b)
                );
                router.moveShape(r.shape, shapeRect);
            }
        }
    }*/

    function renderLink( link ){
        let allPoints = link.path.flat()

        let scale = myState.current.viewport?.scale ?? 1
        let pw = linkArrowSize / scale
        let kLink = myState.current.frameLinks.find(d=>d.attrs.id === link.id)

        if( kLink ){
            kLink.points(allPoints)
            kLink.pointerLength( pw )
            kLink.pointerWidth( pw )
           // kLink.direct( link.mode === 3)
        }else{
            kLink = new RoundedArrow({
                id: link.id,
                direct: link.mode === 3,
                points: allPoints,
                stroke: '#b6b6b6',
                strokeWidth: 0.75,
                pointerLength: pw,
                pointerWidth: pw,
                fill: '#b6b6b6',
                strokeScaleEnabled: false
            })
            myState.current.frameLinks.push(kLink)
            lineLayerRef.current.add( kLink )
        }
        /*const color = link.mode === 3 ? "green" : link.mode === 2 ? "red" : "#555"
        kLink.stroke(color)
        kLink.fill(color)*/

        if( !myState.current.linkFramePending ){
            myState.current.linkFramePending = requestAnimationFrame(()=>{
                if( lineLayerRef.current ){
                    lineLayerRef.current.batchDraw()
                    myState.current.linkFramePending = undefined
                }
            })
        }
    }
    /*function renderLink( avRoute ){
        const edge = myState.current.routing.routerLinks.find(d=>d.route?.g === avRoute)
        if( !edge){return}
        const route = edge.route.displayRoute()
        const points = []
        const sz = route.size()
        for (let i = 0; i < sz ; i++) {
            const { x, y } = route.get_ps(edge.reverse ? sz - i - 1 : i);
            points.push(x)
            points.push(y)
        }

        let allPoints = points

        let scale = myState.current.viewport?.scale ?? 1
        let pw = linkArrowSize / scale
        let link = myState.current.frameLinks.find(d=>d.attrs.id === edge.id)
        if( link ){
            link.points(allPoints)
            link.pointerLength( pw )
            link.pointerWidth( pw )
        }else{
            link = new RoundedArrow({
                id: edge.id,
                points: allPoints,
                stroke: '#b6b6b6',
                strokeWidth: 0.75,
                pointerLength: pw,
                pointerWidth: pw,
                fill: '#b6b6b6',
                strokeScaleEnabled: false
            })
            myState.current.frameLinks.push(link)
            lineLayerRef.current.add( link )
        }
    }*/
    function getRelativeFramePosition(id){
        return myState.current.framePositions?.[id] ?? {x:0,y:0,s:1}
    }
    function getFramePosition(id, defaults = true){
        const item = myState.current.renderList.find(d=>d?.id === id)
        if( item?.parentRender ){
            const relative = myState.current.framePositions?.[id]
            const rx = relative?.x ?? item?.x ?? (defaults ? 0 : undefined)
            const ry = relative?.y ?? item?.y ?? (defaults ? 0 : undefined)
            const rs = relative?.s ?? item?.s ?? (defaults ? 1 : undefined)
            const {x:px, y:py, s:ps} = getFramePosition(item.parentRender, defaults)
            return {
                x: ((rx ?? 0) * (ps ?? 1)) + (px ?? 0),
                y: ((ry ?? 0) * (ps ?? 1)) + (py ?? 0),
                s: (rs ?? 1) * (ps ?? 1)
            }
        }
        const existingFrame = myState.current.frames?.find?.((frame)=>frame?.id === id)
        let x = myState.current.framePositions?.[id]?.x ?? existingFrame?.x ?? item?.x ?? (defaults ? 0 : undefined)
        let y = myState.current.framePositions?.[id]?.y ?? existingFrame?.y ?? item?.y ?? (defaults ? 0 : undefined)
        let s = myState.current.framePositions?.[id]?.s ?? existingFrame?.s ?? existingFrame?.node?.attrs?.scaleX ?? item?.s ?? (defaults ? 1 : undefined)

        return {x,y,s}
    }
    function updateNestedFramePosition( node ){
        const children = myState.current.renderList.filter(d=>d.parentRender === node.attrs.id)
        const startNode = node
        let clipBox
        if( startNode.name().includes("frame")){
            clipBox = {
                x: startNode.attrs.x,
                y: startNode.attrs.y,
                width: startNode.attrs.width,
                height: startNode.attrs.height
            }
        }
        if( children.length > 0){
            let {x:px, y:py, s:ps} = getFramePosition(node.attrs.id)
            for(const d of children){
                const frame = myState.current.frames.find(d2=>d.id === d2.id)
                const node = frame.node
                
                let {x, y, s} = getRelativeFramePosition( d.id)
                x = (x * ps) + px
                y = (y * ps) + py
                s *= ps
                
                node.setPosition({x, y})
                node.scale({x:s, y:s})
                frame.x = x
                frame.y = y

                /*if( clipBox ){
                    const thisClip = {
                        x: Math.max(0, clipBox.x - node.attrs.x),
                        y: Math.max(0, clipBox.y - node.attrs.y),
                        width: Math.min( node.attrs.width * node.attrs.scaleX, (clipBox.x + clipBox.width - node.attrs.x) * node.attrs.scaleX ),
                        height: Math.min( node.attrs.height * node.attrs.scaleX, (clipBox.y + clipBox.height - node.attrs.y) * node.attrs.scaleX )
                    }
                    if( thisClip.x || thisClip.y || (thisClip.width != (node.attrs.width * node.attrs.scaleX)) || (thisClip.height != (node.node.height * node.attrs.scaleX))){
                        node.clip(thisClip)
                    }
                }*/
                
                updateFrameInRouter({
                    id: d.id,
                    x: x,
                    y: y
                })
                
                
            }
        }
    }
    function getNestedFrames(id){
        return myState.current.renderList.filter(d=>d.parentRender === id)
    }

    function orderNestedFrames(node, start = true){
        let children = getNestedFrames( node.attrs.id)
        //let nextZ = startZ ?? node.zIndex() + 1
        if( children.length > 0){
            children = children.sort((a,b)=>(a.canChangeSize === "plain" ? 0 : 1) - (b.canChangeSize === "plain" ? 0 : 1))
            for(const d of children){
                const frame = myState.current.frames.find(d2=>d.id === d2.id)
                if( frame ){
                    const node = frame.node
                    node.moveToTop()
                    orderNestedFrames(node, false)
                }
            }
        }
        if( start ){
            myState.current.frames.forEach(frame=>frame.order = frame.node.zIndex())
            myState.current.frames = myState.current.frames.sort((a,b)=>a.order - b.order)
        }
    }

    function refreshFrame(id, newItems ){
        if( !stageRef.current ){
            return
        }
        const force = newItems !== undefined
        const idx = myState.current.renderList.findIndex(d=>d?.id === id)
        if( idx > -1 ){
            const item = myState.current.renderList[idx]
            if( force ){
                Object.assign(item, newItems)
            }

            const {x, y, s} = getFramePosition(id)
            const {items, title, ...options} = item

            const frame = setupFrameForItems(id, title, items, x, y, s, {...options, resizeForChildren: item.resizeForChildren, parentRender: item.parentRender, forceRender: force})
            if( !frame ){
                return
            }

            buildPositionCache(frame)
            finalizeImages(stageRef.current, {imageCallback: processImageCallback})
            alignViewport(myState.current.viewport?.x ?? 0,myState.current.viewport?.y ?? 0, myState.current.viewport?.scale ?? 1, true)
            orderNestedFrames(frame.node)
            resizeNestedFrame( id )
            refreshLinks([{id: id, x: frame.x, y: frame.y}], true)
                if( myState.current.frameSelect ){
                    if( myState.current.frameSelect.node.attrs.id === id ){
                        clearSelection()                    
                        destroyFrameSelect()
                        createFrameSelect( frame.node )
                }
            }
            stageRef.current.batchDraw()
        }else if( newItems ){
            addFrame(newItems)
        }
    }

    useLayoutEffect(()=>{
        myState.current.renderList = props.render
        myState.current.baseLinks = props.frameLinks
        myState.current.framePositions = props.framePositions
        if( !layerRef.current ){
            return
        }
        var ctx = layerRef.current.getContext()._context;
        ctx.textRendering = "optimizeSpeed";

        if( props.background === "inherit"){
            const rgba = getEffectiveBackgroundRGBA( stageRef.current.container())
            myState.current.background = rgbStringToHex(rgba)
        }else{
            myState.current.background = props.background ?? "white"
        }
        stageRef.current.container().querySelector('canvas').style.background = myState.current.background
        
        
        
        if( myState.current.renderList ){
            if( myState.current.frames ){
                myState.current.frames.forEach(d=>d.markForDeletion = true)
            }
            let x = 0, y = 0, s = 1
            for( const set of myState.current.renderList){
                let {x:tx, y:ty, s:ts} = getFramePosition(set.id, false)
                if( tx !== undefined ){
                    x = tx
                    y = ty
                    s = ts
                    
                }else{
                    if(props.callbacks?.frameMove){
                        props.callbacks.frameMove({
                            id: set.id,
                            x, y, s
                        })
                    }
                }
                const {id, title, items, ...options} = set
                const frame = setupFrameForItems(id, title, items, x, y, s, options)
                if( !frame ){
                    continue
                }
                if( props.layout === "horizontal"){
                    x += (frame.node.attrs.width * frame.node.attrs.scaleX) + 150
                }else{
                    y += (frame.node.attrs.height * frame.node.attrs.scaleY) + 200
                }

            }
            if( myState.current.frames ){
                myState.current.frames = myState.current.frames.filter(d=>{
                    if(d.markForDeletion ){
                        removeRoutingForFrame( d )
                        d.node.destroy()
                        d.border.destroy()
                        return false
                    }
                    return true
                })
                refreshLinks()
            }
        }
        finalizeImages(stageRef.current, {imageCallback: processImageCallback})

        for( const frame of myState.current.frames ?? []){
            orderNestedFrames(frame.node)
        }

        for( const frame of myState.current.frames ?? []){
            buildPositionCache(frame)
        }
        //resizeFrame()
        updateVisibility(0,0)


        const observer = new ResizeObserver((rect)=>{
            markStageRectDirty()
            resizeFrame(rect[0].contentRect.width, rect[0].contentRect.height)
        });
        observer.observe(frameRef.current);


        if( props.primitive?.id !== myState.current.lastPrimitiveId ){
            zoomToExtents()
            myState.current.lastPrimitiveId = props.primitive?.id
        }

        return ()=>{
            
            if( myState.current.timeoutPending ){
                console.log(`Cancelling timeout`)
                clearTimeout(myState.current.timeoutPending)
            }
            if( myState.current.animFramePending ){
                console.log(`Cancelling RAF`)
                cancelAnimationFrame(myState.current.animFramePending)
            }
            myState.current.rescaleList = undefined
            observer.unobserve(frameRef.current);
            for(const frame of myState.current.frames ?? []){
                if( frame.allNodes ){
                    //console.log(`Restoring all nodes on ${frame.id}`)
                    frame.node.children = frame.allNodes
                }
            }
        }
    },[props.update, props.primitive.id, props.renderList, fontsReady])

    function zoomToExtents(){
        if( myState.current?.frames ){
            if( !myState.current.width || !myState.current.height){
                myState.current.needExtentZoom = true
                return 
            }

            const zoomTo = props.initialZoom?.includes("height") ? "height" : props.initialZoom?.includes("width") ? "width" : "full"
            const withTitle = props.initialZoom?.includes("_title")

            myState.current.needExtentZoom = false
            let minX = Infinity, minY = Infinity
            let maxX = -Infinity, maxY = -Infinity
            for( const d of myState.current.frames){
                minX = Math.min( minX, d.x )
                minY = Math.min( minY, d.y )
                maxX = Math.max( maxX, d.x + (d.node.attrs.width * d.node.attrs.scaleX))
                maxY = Math.max( maxY, d.y + (d.node.attrs.height * d.node.attrs.scaleY))
            }
            const w = (maxX - minX)
            let h = (maxY - minY)



            const partial = zoomTo === "width" ? myState.current.width / w : zoomTo === "height" ? myState.current.height / h : Math.min(myState.current.width / w, myState.current.height / h )
            let scale = Math.min(1, 0.95 * partial)

            if( withTitle && zoomTo === "height"){
                minY -= (14 * 25)
                h = (maxY - minY)
                scale = myState.current.height  / (h ) * 0.99
            }

            let ox = -minX * scale, oy = -minY * scale
            let tx = ox + enableYPan ? ((myState.current.width  - (w * scale)) /2) : 0
            let ty = oy + (zoomTo === "width"  ? + (w * 0.05) : ((myState.current.height  - (h * scale)) /2))

            const viewport = alignViewport(tx, ty, scale, true)
            if( props.singleFrame ){
                const centerX = (minX + maxX) / 2
                const centerY = (minY + maxY) / 2
                myState.current.singleFrameInfo = {
                    base: {...viewport},
                    bounds: {minX, minY, maxX, maxY, centerX, centerY}
                }
            }
            rescaleLinks()
        }
    }


    function calcPage(x,y){
        return [Math.floor(x / chunkWidth),  Math.floor(y / chunkHeight)]
    }
    function areSetsEqual(set1, set2) {
        if (set1.size !== set2.size) {
          return false;
        }
        for (let item of set1) {
          if (!set2.has(item)) {
            return false;
          }
        }
        return true;
      }
      

    function updateVisibility(x,y){
        
        if(!stageRef.current || !myState.current.frames){
            return
        }        
        let nodes =  layerRef.current.children
        let topLayer = []
        let scale = stageRef.current.scale().x
        x = x 
        y = y 
        let w = myState.current.width / scale
        let h = myState.current.height / scale
        let idx = 0
        let vis = 0
        let x2 = x + w
        let y2 = y + h

        const lastRenderCount = myState.current.refreshCount
        myState.current.refreshCount = (myState.current.refreshCount ?? 0) + 1

        let changed = 0
        if( enablePages ){
            let lastPages = enableNodePruning ? undefined : myState.current.lastRendered ?? []
            if( enableFlipping ){
                w *= 2
                h *= 2
            }
                

            const cols = Math.ceil(w / chunkWidth) + 1
            const rows = Math.ceil(h / chunkHeight) + 1

            w = cols * chunkWidth
            h = rows * chunkHeight

            x2 = x + w
            y2 = y + h
            
            //  console.log(`Will render ${cols} x ${rows} pages from ${px}, ${py}`)
            for(const frame of myState.current.frames){
                const [px, py] = calcPage(x - frame.node.attrs.x, y - frame.node.attrs.y)
                
                const seenPages = new Set();
                const pages = []


                let spx = Math.max(0, px)
                let spy = Math.max(0, py)
                let epx = Math.min(frame.maxCol + 1, px + cols + 1)
                let epy = Math.min(frame.maxRow + 1, py + rows + 1)



             //   console.log(`Frame ${frame.id} offset by ${px}, ${py} pages - : ${spx}, ${spy} - doing ${epx-spx} x ${epy-spy} pages`)

                for(let j = spy; j < epy; j++){
                    for(let i = spx; i < epx; i++){
                        const page = `${i}-${j}`
                        if (!seenPages.has(page)) {
                            seenPages.add(page);
                            pages.push( page )
                        }
                    }
                }
                
                let pagesToProcess
                if( enableFlipping ){
                    if( enableNodePruning ){
                        pagesToProcess = pages//.filter((d,i,a)=>a.indexOf(d)===i)
                    }else{
                        pagesToProcess = [lastPages.filter(d=>!pages.includes(d)), pages.filter(d=>!lastPages.includes(d))].flat()//.filter((d,i,a)=>a.indexOf(d)===i)
                    }
                }else{
                    pagesToProcess = [lastPages, pages].flat().filter((d,i,a)=>a.indexOf(d)===i)
                }
                
                let hidden = 0
                const seen = new Set();
                nodes = [];
                topLayer = []
                pagesToProcess.forEach(d => {
                    const [x, y] = d.split("-");
                    const n = frame.pages[x]?.[y];
                    if( n){
                        n.forEach(item => {
                            const uniqueKey = item._id; // Assuming 'item' has a unique 'id'
                            if (!seen.has(uniqueKey)) {
                                seen.add(uniqueKey);
                                if( item.attrs.scaleFont || ((item.width() * scale * frame.scale) > (item.attrs.minRenderSize ?? 8)) || item.attrs.name === "frame_outline"){
                                    if( item.attrs.name?.includes("top_layer")){
                                        topLayer.push(item)
                                    }else{
                                        nodes.push(item);
                                    }
                                }else{
                                    hidden++
                                }
                            }
                        });
                    }
                });
                
                
                if( !enableNodePruning ){
                    myState.current.lastRendered = pages
                }
                
                
                const activeNodes = []
                const newThisRender = []
               // const activeNodeIds = new Set()
                let newlyRendered = 0

                vis = 0
                let ax = x - frame.node.attrs.x
                let ax2 = (x2 - frame.node.attrs.x)
                let ay = y - frame.node.attrs.y
                let ay2 = (y2 - frame.node.attrs.y) 

                for( const set of [nodes, topLayer]){
                    for( const d  of set){
                        let c = d.attrs
                        let x1 = c.x 
                        let y1 = c.y 
                        let x2 = c.x + c.width
                        let y2 = c.y + c.height
                        if(  frame.scale !== 1){
                            x1 *= frame.scale
                            x2 *= frame.scale
                            y1 *= frame.scale
                            y2 *= frame.scale
                        }
                        let visible = true//c.scaleFont ? (x1 >= ax &&  x1 <= ax2 && (y2 + c.offsetY) >= ay && y2 <= ay2) : (x2 > ax &&  x1 < ax2 && y2 > ay && y1 < ay2)
                        if(visible){
                            if( d.attrs.scaleFont ){
                                const iScale = Math.min(25, Math.max(1 / scale / frame.scale , 2))
                                if( d.attrs.hideScale){
                                    d.visible(iScale > d.attrs.hideScale)
                                }
                                if( frame.label && d._id === frame.label._id ){
                                    layoutFrameLabel(frame, iScale)
                                }
                            }
                            vis++
                            if( enableNodePruning ){
                                if( lastRenderCount === undefined || (d.attrs._vis !== lastRenderCount)){
                                    newThisRender.push(d)
                                    newlyRendered++
                                }
                                d.attrs._vis = myState.current.refreshCount
                                
                                if( d.attrs["_txc"] !== myState.current.transformCount){
                                    d._clearSelfAndDescendantCache('absoluteTransform')
                                    d.attrs["_txc"] = myState.current.transformCount
                                }
                                activeNodes.push(d)
                            }else{
                                d.visible(visible)
                            }
                        }
                    }
                }
                if( !frame.lastNodeIds){
                    changed = changed > 1 ? changed : 1
                }else if(newlyRendered  ){
                    changed = 2
                }

                frame.lastNodes = activeNodes
                frame.thisRender = newThisRender

                let showFrame = hidden > 0
                if( props.board){
                    //frame.border.stroke(showFrame ? "#b8b8b8" : "#b8b8b8")
                    //frame.border.fill(showFrame ? "#f2f2f2" : "#efefef")
                }else{
                    frame.border.visible(showFrame)
                }
                
                if( enableNodePruning ){
                    frame.node.children = activeNodes
                }
            //    console.log(`${frame.id} RENDERED ${vis} of ${nodes.length} candidates / ${myState.current.frames.map(d=>d.allNodes.length).reduce((a,c)=>a+c,0)}`)
            }
            return changed
        }else{
            for( const d  of nodes){
                let c = d.attrs
                let visible = ((c.x + c.width) > x &&  c.x < x2 && (c.y + c.height) > y && c.y < y2)
                if(visible){
                    vis++
                    d.visible(visible)
                }
            }
            console.log(`RENDERED ${vis} of ${nodes.length} candidates / ${myState.current.frames.map(d=>d.allNodes.length).reduce((a,c)=>a+c,0)}`)
        }

        
    }

    function computeSingleFrameBounds(){
        const frames = myState.current.frames
        if( !frames || frames.length === 0){
            return undefined
        }
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for( const frame of frames){
            minX = Math.min(minX, frame.x)
            minY = Math.min(minY, frame.y)
            maxX = Math.max(maxX, frame.x + (frame.node.attrs.width * frame.node.attrs.scaleX))
            maxY = Math.max(maxY, frame.y + (frame.node.attrs.height * frame.node.attrs.scaleY))
        }
        if( !isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)){
            return undefined
        }
        const centerX = (minX + maxX) / 2
        const centerY = (minY + maxY) / 2
        return {minX, minY, maxX, maxY, centerX, centerY}
    }
    function constrainViewportForSingleFrame(tx, ty, scale){
        if( !props.singleFrame ){
            return {x: tx, y: ty, scale}
        }
        const info = myState.current.singleFrameInfo ?? {}
        const bounds = info.bounds ?? computeSingleFrameBounds()
        const width = myState.current.width
        const height = myState.current.height
        if( !bounds || !width || !height ){
            if( info.base ){
                return {x: info.base.x, y: info.base.y, scale}
            }
            return {x: tx, y: ty, scale}
        }
        if( !info.bounds && bounds ){
            info.bounds = bounds
            myState.current.singleFrameInfo = info
        }
        const base = info.base
        const {
            minX,
            minY,
            maxX,
            maxY,
            centerX: boundsCenterX,
            centerY: boundsCenterY
        } = bounds
        let newX = tx
        let newY = ty

        if( base && scale <= (base.scale ?? scale) + SINGLE_FRAME_EPSILON ){
            const cX = boundsCenterX ?? ((minX + maxX) / 2)
            const cY = boundsCenterY ?? ((minY + maxY) / 2)
            return {
                x: (width / 2) - (cX * scale),
                y: (height / 2) - (cY * scale),
                scale
            }
        }

        const minViewportX = width - (maxX * scale)
        const maxViewportX = -(minX * scale)
        if( minViewportX > maxViewportX ){
            const sceneCenterX = (boundsCenterX ?? ((minX + maxX) / 2)) * scale
            newX = (width / 2) - sceneCenterX
        }else{
            newX = Math.max(minViewportX, Math.min(maxViewportX, tx))
        }

        const minViewportY = height - (maxY * scale)
        const maxViewportY = -(minY * scale)
        if( minViewportY > maxViewportY ){
            const sceneCenterY = (boundsCenterY ?? ((minY + maxY) / 2)) * scale
            newY = (height / 2) - sceneCenterY
        }else{
            newY = Math.max(minViewportY, Math.min(maxViewportY, ty))
        }

        return {x: newX, y: newY, scale}
    }
    function alignViewport(tx,ty, scale, forceRescale = false){
        if( props.singleFrame ){
            const constrained = constrainViewportForSingleFrame(tx, ty, scale)
            tx = constrained.x
            ty = constrained.y
        }
        let x = tx, y = ty

        function quantizeFrame(x,y){            
            return [
                Math.floor(x / (myState.current.width / 2)) * (myState.current.width / 2),
                Math.floor(y / (myState.current.height / 2)) * (myState.current.height / 2)
            ]
        }

        if( enableFlipping){
            let doUpdate = false, updateStagePosition = false
            myState.current.flipping ||= {last: {scale: scale, fhx: 0, fhy: 0, fsx:0, fsy: 0, skx: 0, sky: 0}}

            const tw = myState.current.width
            const th = myState.current.height


            let fx = -tx / myState.current.flipping.last.scale * scale 
            let fy = -ty / myState.current.flipping.last.scale * scale

            let fhx = Math.floor(((fx ) - (myState.current.flipping.last.fsx / myState.current.flipping.last.scale * scale) ) / tw) 
            let fhy = Math.floor(((fy ) - (myState.current.flipping.last.fsy / myState.current.flipping.last.scale * scale) ) / th) 


            let cx = Math.floor(-tx / scale / chunkWidth) * chunkWidth
            let cy = Math.floor(-ty / scale / chunkHeight) * chunkHeight

            const isZooming = myState.current.flipping.last.callScale !== scale


            const refScale = scale / (myState.current.flipping.last.scale ?? 1)
            let doImageRescale = false
            
            if( isZooming || forceRescale){
                if( refScale > scaleTriggers.max || refScale < scaleTriggers.min || forceRescale || myState.current.flipping.last.fhx !== fhx || myState.current.flipping.last.fhy !== fhy){
                    //console.log(`RESET ZOOM`, cx, stageRef.current.x(), scale)
                    myState.current.flipping.last.scale = scale
                    
                    let q = quantizeFrame( -tx, -ty)
                    fx = q[0]
                    fy = q[1]
        
                    myState.current.flipping.last.fsx = fx
                    myState.current.flipping.last.fsy = fy

                    fhx = Math.floor(((fx ) - (myState.current.flipping.last.fsx / myState.current.flipping.last.scale * scale) ) / tw) 
                    fhy = Math.floor(((fy ) - (myState.current.flipping.last.fsy / myState.current.flipping.last.scale * scale) ) / th) 

                    myState.current.flipping.last.fhx = fhx
                    myState.current.flipping.last.fhy = fhy
                                    
                    stageRef.current.scale({x:scale, y:scale})
                    doUpdate = true
                    updateStagePosition = true
                    doImageRescale = forceRescale

                    rescaleLinks()

                   /* if( myState.current.hover ){
                        for( const d of Object.keys(myState.current.hover)){
                            const node = myState.current.hover[d]
                            if( node ){
                                leftNode(node, d )
                                enteredNode(node, d, scale )
                            }
                        }
                    }*/
                    
                }
            }
           if( !isZooming ){
                if(myState.current.flipping.last.fhx !== fhx || myState.current.flipping.last.fhy !== fhy){
                    let q = quantizeFrame( fx, fy)
                    
                    myState.current.flipping.last.fsx = q[0]
                    myState.current.flipping.last.fsy = q[1]


                    fhx = Math.floor(((fx ) - (myState.current.flipping.last.fsx / myState.current.flipping.last.scale * scale) ) / tw) 
                    fhy = Math.floor(((fy ) - (myState.current.flipping.last.fsy / myState.current.flipping.last.scale * scale) ) / th) 

                    myState.current.flipping.last.fhx = fhx
                    myState.current.flipping.last.fhy = fhy

                    doUpdate = true
                    updateStagePosition = true
                }
            }

            myState.current.flipping.skx = ((myState.current.flipping.last.fsx / myState.current.flipping.last.scale) ) - cx 
            myState.current.flipping.sky = ((myState.current.flipping.last.fsy / myState.current.flipping.last.scale) ) - cy 

            const vx = (cx  + myState.current.flipping.skx) * scale
            const vy = (cy  + myState.current.flipping.sky) * scale
           
            if( !isZooming ){
                if(myState.current.flipping.last.cx !== cx || myState.current.flipping.last.cy !== cy){
                    doUpdate = true
                    myState.current.flipping.last.cx = cx
                    myState.current.flipping.last.cy = cy
                }
            }

            if( doUpdate ){
                myState.current.redrawingForZoomPan = true
                let hasChanged = updateVisibility(cx, cy)

                if( updateStagePosition ){
                    myState.current.transformCount = (myState.current.transformCount || 0) + 1
                    stageRef.current.position({x:-vx, y: -vy})
                    stageRef.current.batchDraw()
                    if( myState.current.frameLinks ){
                        const transformMark = myState.current.transformCount
                        for( const link of myState.current.frameLinks ){
                            if( link?.attrs?._txc !== transformMark ){
                                link?._clearSelfAndDescendantCache?.('absoluteTransform')
                                link.attrs._txc = transformMark
                            }
                        }
                    }

                    if( myState.current.routing?.router){
                        if( scale !== myState.current.routing.linkScale ){
                            myState.current.routing.linkScale = scale 
                            myState.current.routing.router.setScale(myState.current.routing.linkScale * 2 )
                        }
                        const blur = 50
                        let w = myState.current.width * 2 / scale
                        let h = myState.current.height * 2 / scale
                        if( w < chunkWidth * 2){ 
                            w = chunkWidth * 2
                        }
                        if( h < chunkHeight * 2){ 
                            h = chunkHeight * 2
                        }
                        myState.current.routing.router.setFocus({
                                left: cx, 
                                top: cy, 
                                width: w, 
                                height: h,
                                blur})
                        myState.current.routing.router.route()
                    }

                }else if( hasChanged ){
                    requestAnimationFrame(()=>{
                        myState.current.frames.forEach((f)=>{
                            if( f.thisRender){
                                for(const d of f.thisRender){
                                    d.lastActualRenderFor = d.attrs._vis
                                    d.draw()
                                }
                            }
                        })
                    })
                }
                myState.current.redrawingForZoomPan = false
            }


            let ox = (-tx - (myState.current.flipping.last.fsx / myState.current.flipping.last.scale * scale )) // (scale / myState.current.flipping.last.scale )
            let oy = (-ty - (myState.current.flipping.last.fsy / myState.current.flipping.last.scale * scale)) // (scale / myState.current.flipping.last.scale)
            

            let ns = scale / myState.current.flipping.last.scale
            stageRef.current.container().style.transform = `translate(${-Math.floor(ox)}px, ${-Math.floor(oy)}px) scale(${ns}) `
            myState.current.flipping.last.callScale = scale

            myState.current.viewport = {x: tx, y: ty, scale: scale}
            myState.current.stageTransform = {x: -ox, y: -oy, s: ns}
            markStageRectDirty()

            const topLeft = convertStageCoordToScene(0, 0)
            const bottomRight = convertStageCoordToScene(myState.current.width, myState.current.height)
            myState.current.userView = {
                x: topLeft[0],
                y: topLeft[1],
                r: bottomRight[0],
                b: bottomRight[1]
            }


            return myState.current.viewport

        }else{
            stageRef.current.scale({x: scale, y: scale} )
            stageRef.current.x(x)
            stageRef.current.y(y)
            updateVisibility(-x, -y)
            stageRef.current.batchDraw()
            markStageRectDirty()
            
            return {x:tx,y:ty, scale:scale}
        }

    }

    function restoreScale(){
        return myState.current.viewport?.scale ?? 1
    }
    function restoreTransform(){
        return [myState.current.viewport?.x ?? 0, myState.current.viewport?.y ?? 0, myState.current.viewport?.scale ?? 1]
    }
    function createDragging(clone, x, y, shadow){
        if( myState.current.dragging){
            throw "Dragging already present"
            return
        }
        const container = document.createElement('div')
        container.setAttribute("id","drag_container")
        Object.assign(container.style,{
            top:0,
            left: 0,
            position: "absolute"
        })
        frameRef.current.append(container)


        const w = clone.x() + ((clone.width() + shadow)* clone.scale().x)
        const h = clone.y() + ((clone.height() + shadow )* clone.scale().y)

        const maxW = myState.current.width + x
        const maxH = myState.current.height + y

        console.log(`Canvas size => ${x} => ${maxW}`)
        console.log(`Canvas size => ${y} => ${maxH}`)



        const stage = new Konva.Stage({width: Math.min(w, maxW) , height: Math.min(h, maxH), container:'drag_container'})
        //const stage = new Konva.Stage({width: frameRef.current.offsetWidth, height: frameRef.current.offsetHeight, container:'drag_container'})
        stage.add(new Konva.Layer())
        
        myState.current.dragging = {
            clone: clone,
            stage: stage,
            later: stage.children[0],
            ox: x,
            oy: y,
            sw: w,
            sh: h
        }

        return stage
    }
    function destroyDragging(){
        if( !myState.current.dragging){
            return
        }
        let container = myState.current.dragging.stage.container()
        myState.current.dragging.stage.destroy()
        container.remove() 
        myState.current.dragging = undefined



    }
    function panHandler(state){
        if( state.first || state.memo === undefined) {
            clearHightlights()
            if( props.callbacks?.viewportWillMove){
                props.callbacks?.viewportWillMove(myState.current.viewport)
            }
        }
        const multiple = myState.current.panForDrag ? -1 : 3
        // Cap per-event pan to prevent large jumps from discrete wheel mouse notches
        // (deltaY ≈ 100 × 3 = 300px). Trackpad fires many small deltas (< 40px × 3 = 120px)
        // and won't hit this cap during normal use.
        const maxStep = 200
        const dx = Math.max(-maxStep, Math.min(maxStep, state.delta[0] * multiple))
        const dy = Math.max(-maxStep, Math.min(maxStep, state.delta[1] * multiple))

        let x = enableXPan ? (myState.current.viewport?.x ?? 0) - dx : myState.current.viewport?.x ?? 0
        let y = enableYPan ? (myState.current.viewport?.y ?? 0) - dy : myState.current.viewport?.y ?? 0

        if( enableXPan && !enableYPan ){
            const last = myState.current.frames.at(-1)?.node
            if( last ){
                const br = last.attrs.x + (last.attrs.width * last.attrs.scaleX)
                const sbr = (br * myState.current.viewport.scale)
                if( myState.current.width >= sbr + x ){
                    x =  myState.current.width - sbr
                }
            }
            if( x > 0){
                x = 0
            }
        }
        
        alignViewport(x,y, myState.current.viewport?.scale ?? 1)

        if( state.last ){
            let [px, py] = convertEventToScene(state.event)
            processHighlights(px,py)
            myState.current.wasPanning = false
            myState.current.panForDrag = false
            if( state.last ){
                if( props.callbacks?.viewportCallback){
                    props.callbacks?.viewportCallback(myState.current.viewport)
                }
            }
        }
        
    }

    

    useGesture({
        onDrag:(state)=>{
            if( myState.current.frameSelect?.transforming){
                return
            }
            if( props.enableTextSelection && myState.current.textSelection?.dragging ){
                updateTextSelectionFromEvent(state.event)
                return
            }
            if( props.enableTextSelection && state.first ){
                const scale = restoreScale()
                const frameRect = frameRef.current.getBoundingClientRect()
                let [px, py] = state.initial
                px -= frameRect.left
                py -= frameRect.top
                const [sx, sy] = convertStageCoordToScene(px, py)
                const textNode = findCustomTextAtPosition(sx, sy)
                if( textNode ){
                    startTextSelection(textNode, state.event)
                    return
                }
            }
            if( myState.current.panForDrag ){
                return panHandler(state)
            }
            let memo = state.memo || {}
            let x, y
            if( state.first ){
                const scale = restoreScale()
                var frameRect = frameRef.current.getBoundingClientRect();
                let [px,py] = state.initial
                memo = {x: frameRect.left, y: frameRect.top}

                px -= memo.x;
                py -= memo.y;


                [x, y] = convertStageCoordToScene(px, py )
                let found = orderInteractiveNodes(findTrackedNodesAtPosition( x, y, ["primitive", "frame",  "pin"], true, false, true))
                let item = found[0]
                if( !item ){
                    myState.current.panForDrag = true
                    return panHandler(state)
                }
                if( !enableDrag ){
                    myState.current.panForDrag = true
                    return panHandler(state)
                }
                if( item && !myState.current.dragging ){

                    clearHightlights()
                    clearSelection(["frame"])                    
                    destroyFrameSelect()
                    if( props.callbacks?.viewportWillMove){
                        props.callbacks?.viewportWillMove(myState.current.viewport)
                    }
                    
                    let isPrimitive = item.attrs.name?.includes( "primitive")
                    let isFrame = item.attrs.name === "frame"
                    let isPin = !isFrame && item.attrs.name?.includes("pin")

                    if( isPrimitive ){
                        if( props.drag?.primitive?.start ){
                            const parentFrame = item.findAncestor(".frame")
                            if( parentFrame ){
                                const dragTarget = props.drag.primitive.start( item.attrs.id, parentFrame?.attrs.id )
                                if( dragTarget === "frame_parent"){
                                    item = parentFrame
                                    isFrame = true
                                    isPrimitive = false
                                }
                            }
                        }
                        
                    }
                    
                    let clone
                    let offsetForShadow = 10
                    let dx, dy, minX, minY
                    let frameScale = (isFrame ? item.attrs.scaleX : item.findAncestor('.frame')?.attrs.scaleX) 
                    let cloneScale = scale * frameScale

                    if( isFrame ){

                        clone = new Konva.Group({})
                        minX = Infinity
                        minY = Infinity
                        let maxX = -Infinity, maxY = -Infinity
                        let clones = []
                        
                        myState.current.selected = myState.current.selected ?? {}


                        
                        const frameInList = myState.current.selected.frame?.find(d=>d.attrs.id === item.attrs.id)
                        if( !frameInList ){
                            if(state.event.shiftKey == false || !props.selectable?.frame?.multiple){
                                myState.current.selected.frame = [item]
                            }else{
                                myState.current.selected.frame.push(item)
                            }                            
                        }
                        let oScale
                        for(const item of myState.current.selected.frame ){
                            oScale = oScale ?? item.attrs.scaleX
                            
                            const thisClone = item.clone()                    
                            minX = Math.min( minX, item.attrs.x)
                            minY = Math.min( minY, item.attrs.y)
                            maxX = Math.max( maxX, item.attrs.x + (item.attrs.width * item.attrs.scaleX))
                            maxY = Math.max( maxY, item.attrs.y + (item.attrs.height * item.attrs.scaleY))
                            thisClone.find('.frame_label')?.[0]?.destroy()
                            thisClone.find('#frame')?.[0]?.stroke('#999')
                            item.visible(false)
                            thisClone.clip({
                                x: undefined,
                                y: undefined,
                                width: undefined,
                                height: undefined
                            })
                            clones.push( thisClone)
                        }
                        for(const d of clones){
                            d.x( d.x() - minX)
                            d.y( d.y() - minY)
                            clone.add(d)
                        }
                        clone.x( minX )
                        clone.y( minY )
                        clone.width( (maxX - minX ) * 1.05)
                        clone.height( (maxY - minY) * 1.05)
                        offsetForShadow = 0

                        dx = x - minX
                        dy = y - minY
                        cloneScale = scale 
                    }else if(isPin){
                            const sourcePinName = item.attrs.id
                            const [sourceItem, sourcePin] = sourcePinName.split("_")
                            const shapeForPin = addShapeToRouter( "pin" , x, y, x + 2, y +2, [{idx: 1, x: 0.5, y:0.5, dir: 15}])
                            const sourceFrame = myState.current.frames.find(d2=>d2.id === sourceItem)
                            const sourceShape = sourceFrame.routing[sourceItem]?.shape
                            const pinDef = [...sourceFrame.pins.input, ...sourceFrame.pins.output].find(d=>d.idx == sourcePin)
                            const reverse = !pinDef.output
                            const rightSide = pinDef.internal ? pinDef.output : !pinDef.output

                            const connectTo = pinDef.output ? "input" : "output"
                            

                            const pinPoint = {
                                shape: {id: "pin"},
                                side: rightSide ? "right" : "left",
                                distance: 0.5
                            }
                            const shapePoint = {
                                shape: {id: sourceItem},
                                side: rightSide ? "left" : "right" ,
                                distance: pinDef.y / sourceFrame.node.attrs.height
                            }

                            const link = {
                                id: "pin_drag",
                                pointA: reverse ? pinPoint : shapePoint,
                                pointB: reverse ? shapePoint : pinPoint
                            }
                            
                            myState.current.routing.router.addLink( link )

                            /*myState.current.routing.routerLinks.push({
                                id: "pin_drag",
                                reverse: !pinDef.output,
                                route: connectorForPin
                            })*/

                            const pinCache = []
                            for(const frame of myState.current.frames){
                                if(frame.pins){
                                    for(const pin of frame.pins[connectTo]){
                                        pinCache.push({
                                            frame: frame.id,
                                            pinId: pin.idx,
                                            x: frame.node.attrs.x + (pin.node.attrs.x + (pin.node.attrs.width / 2)) * frame.node.attrs.scaleX,
                                            y: frame.node.attrs.y + (pin.node.attrs.y + (pin.node.attrs.height / 2)) * frame.node.attrs.scaleY,
                                            cx: frame.node.attrs.x + (pin.node.attrs.x + (pin.node.attrs.width / 2)) * frame.node.attrs.scaleX,
                                            cy: frame.node.attrs.y + (pin.node.attrs.y + (pin.node.attrs.height / 2)) * frame.node.attrs.scaleY,
                                            
                                        })
                                    }
                                }
                            }
                            
                            myState.current.dragging = {
                                connectTo,
                                sourcePin: pinDef.name,
                                sourceItem,
                                shapeForPin,
                                //connectorForPin,
                                pinCache
                            }
                            console.log( myState.current.dragging )
                    }else{
                        cloneScale = item.scaleX() * cloneScale
                        clone = item.clone()                    
                        const box = new Konva.Rect({
                            x: 0,
                            y: 0,
                            width: clone.width(),
                            height: clone.height(),
                            fill: 'white',
                            cornerRadius:2,
                            stroke: colors.select.border.stroke,
                            strokeWidth:0.5,
                            shadowColor: "#666",
                            shadowOffset: {x:2, y:2},
                            shadowBlur: 4,
                        })
                        clone.add(box)
                        box.zIndex(0)
                        for(const i of clone.find('Image')){
                            i.clearCache()
                        }
                        
                        dx = x - (item.parent?.attrs.x ?? 0) - (item.attrs.x * frameScale)
                        dy = y - (item.parent?.attrs.y ?? 0) - (item.attrs.y * frameScale)
                    }
                    if( clone ){

                        
                        clone.scale({x: cloneScale, y:cloneScale})
                        clone.setAttrs({
                            x: offsetForShadow,
                            y: offsetForShadow,                    
                        })
                        clone.find('.frame_outline').forEach(d=>d.stroke("#b8b8b8"))
                        clone.find('.frame_bg').forEach(d=>d.fill("white"))
                        
                        
                        createDragging(clone, (dx * scale) + offsetForShadow, (dy * scale) + offsetForShadow, 8 )
                        myState.current.dragging.stage.children[0].add(clone)
                        
                        if( props.enableFrameSelection){
                            myState.current.dragging.frameSelect = [item]
                        }

                        const frameItem = myState.current.frames?.find(d2=>d2.id === item.attrs.id)
                        if( frameItem?.parentRender ){
                            const p = framePosition(frameItem.parentRender)
                            myState.current.dragging.parentBounds = [p.scene.l, p.scene.t, p.scene.r, p.scene.b]
                            myState.current.dragging.snaps = getAnchorLinesForNestedFrame( frameItem )

                            myState.current.frames.forEach(d=>{
                                if( d.isContainer && d.id !== frameItem.parentRender){
                                    const p = framePosition(d.id)
                                    let visible = ((p.scene.r) > myState.current.userView.x &&  p.scene.l < myState.current.userView.r && p.scene.b > myState.current.userView.y && p.scene.t < myState.current.userView.b)
                                    if( visible ){
                                        getAnchorLinesForFrame( d.id, undefined, myState.current.dragging.parentBounds, myState.current.dragging.snaps.segments.v, myState.current.dragging.snaps.segments.h)
                                    }
                                }
                            })

                            myState.current.dragging.snaps.v = Object.keys(myState.current.dragging.snaps.segments.v)
                            myState.current.dragging.snaps.h = Object.keys(myState.current.dragging.snaps.segments.h)
                            

                            myState.current.dragging.anchorLinesParent = renderAnchorLines( myState.current.dragging.snaps, frameItem.parentRender)
                            console.log(myState.current.dragging.snaps)
                            if( !myState.current.dragging.anchorLinesParent ){
                                debugger
                            }
                        }
                        
                        myState.current.dragging.sourceItem = item
                        myState.current.dragging.isFrame = isFrame
                        myState.current.dragging.startScale = scale
                        myState.current.dragging.minX = minX
                        myState.current.dragging.minY = minY
                        myState.current.dragging.spx = px
                        myState.current.dragging.spy = py
                        myState.current.dragging.sox = myState.current.dragging.ox
                        myState.current.dragging.soy = myState.current.dragging.oy
                        myState.current.dragging.type = "primitive"
                        myState.current.dragging.stage.batchDraw()
                        myState.current.dragging.stage.container().style.transform = `translate(${px - myState.current.dragging.ox}px, ${py - myState.current.dragging.oy}px)`
                    }
                    
                    
                }
            }else{
                if( !myState.current.dragging ){
                    return
                }
                let [px,py] = state.xy
                px -= memo.x
                py -= memo.y

                if( myState.current.dragging.sourcePin  ){
                    let [fx, fy] = convertStageCoordToScene(px, py)
                    let ofx = fx, ofy = fy

                    let threshold = 10
                    let closestPin = myState.current.dragging.pinCache.reduce((a,c)=>{
                        const distance = Math.sqrt( ((fx - c.x) ** 2) + ((fy - c.y) ** 2))
                        if( distance < threshold){
                            if( distance < a.d){
                                a.d = distance
                                a.pin = c
                            }
                        }
                        return a
                    }, {d: Infinity}).pin
                    
                    let revertPosition = undefined
                    if( closestPin ){
                        [fx, fy] =[closestPin.cx, closestPin.cy ]
                    }
                    
                    let found = findTrackedNodesAtPosition( fx, fy, "pin" )?.[0]
                    if( found){
                        if( myState.current.dragging.lastFound !== found){
                            const config = props.drag?.pin?.pin
                            if( config?.droppable ){
                                let canConnect
                                const [otherItem, otherPinIdx] = found.attrs.id?.split("_")
                                const otherFrame =  myState.current.frames.find(d2=>d2.id === otherItem)
                                const otherPinDef = [...otherFrame.pins.input, ...otherFrame.pins.output].find(d=>d.idx == otherPinIdx)
                                const otherPin = otherPinDef?.name
                                if( otherItem && otherPin ){
                                    if( myState.current.dragging.connectTo === "input"){
                                        if( !otherPinDef.output || otherPinDef.internal){
                                            myState.current.dragging.dropData = [myState.current.dragging.sourceItem, myState.current.dragging.sourcePin, otherItem, otherPin ]
                                        }
                                    }else{
                                        if( otherPinDef.output && !otherPinDef.internal){
                                            myState.current.dragging.dropData = [otherItem, otherPin, myState.current.dragging.sourceItem, myState.current.dragging.sourcePin]
                                        }
                                    }
                                    
                                    canConnect = myState.current.dragging.dropData ? config.droppable( ...myState.current.dragging.dropData ) : false
                                    if( canConnect ){
                                        doHighlight(found, "pin")
                                    }else{
                                        revertPosition = true
                                    }
                                    myState.current.dragging.lastFoundRevert = revertPosition
                                }
                            }
                        }else{
                            revertPosition = myState.current.dragging.lastFoundRevert
                        }
                    }else{
                        clearHightlight("pin")
                        myState.current.dragging.dropData = undefined
                    }
                    if( revertPosition === true){
                        clearHightlight("pin")
                        fx = ofx
                        fy = ofy
                    }
                    myState.current.dragging.lastFound = found

                    refreshLinksDuringDrag([
                            {id: "pin", x: fx, y: fy}
                        ])
                    
                    if( state.last ){
                        myState.current.routing.router.removeShape( {id: "pin"} )

                        if( myState.current.dragging.dropData ){
                            const config = props.drag?.pin?.pin
                            if( config?.drop ){
                                const dropped = config.drop( ...myState.current.dragging.dropData )
                            }
                        }

                        myState.current.dragging = undefined
                    }
                    return memo
                }
               if( myState.current.dragging.snaps && !state.event.shiftKey){
                    const snaps = myState.current.dragging.snaps
                    let [fx, fy] = convertStageCoordToScene(px - myState.current.dragging.ox, py - myState.current.dragging.oy)
                    const sx = snapTo( fx, snaps.v )
                    const sy = snapTo( fy, snaps.h )
                    let [ox, oy] = convertSceneCoordToScreen(sx, sy)
                    showAnchorLines( myState.current.dragging.anchorLinesParent, fx, fy, "vh", sx, sy, snaps, myState.current.dragging.parentBounds)
                    px = ox + myState.current.dragging.ox
                    py = oy + myState.current.dragging.oy
                }

                myState.current.dragging.spx = px
                myState.current.dragging.spy = py
                myState.current.dragging.stage.container().style.transform = `translate(${px - myState.current.dragging.ox}px, ${py - myState.current.dragging.oy}px)`;
                [x, y] = convertStageCoordToScene(px, py )

                if( updateLinksDuringMove && myState.current.dragging.isFrame){
                    const [fx, fy] = convertStageCoordToScene(px - myState.current.dragging.ox, py - myState.current.dragging.oy)
                    refreshLinksDuringDrag(
                        myState.current.selected.frame.map(d=>(
                            {id: d.attrs.id, x: fx + d.attrs.x -  myState.current.dragging.minX, y: fy + d.attrs.y -  myState.current.dragging.minY}
                        ))                        
                    )
                }
            }
            if(state.last){
                myState.current.ignoreClick = props.ignoreAfterDrag || !myState.current.dragging
            }
            
            if( myState.current.dragging ){
                const config = props.drag?.[myState.current.dragging.type]
                if( config ){
                    for(const type of Object.keys(config)){
                        let found = findTrackedNodesAtPosition( x, y, type )?.[0]
                        if( state.first ){
                            myState.current.dragging.startZone = found
                        }
                        if( found ){
                            if( config[type].droppable && myState.current.dragging.startZone ){
                                let droppable
                                if( props.board ){
                                    const startFrame = myState.current.dragging.startZone?.findAncestor('.frame')?.attrs.id
                                    const dropFrame = found.findAncestor('.frame')?.attrs.id
                                    droppable = config[type].droppable( myState.current.dragging.clone.attrs.id, myState.current.dragging.startZone.attrs.id, found.attrs.id, startFrame, dropFrame)
                                }else{
                                    droppable = config[type].droppable( myState.current.dragging.clone.attrs.id, myState.current.dragging.startZone.attrs.id, found.attrs.id)
                                }
                                if( !droppable){
                                    console.log(`Cant drop on ${found.attrs.id}`)
                                    continue
                                }
                            }
                            doHighlight(found, type, "drop_")
                        }
                        myState.current.dragging.dropZone = found
                        myState.current.dragging.dropConfig = config[type]
                    }
                }
                
                if(state.last){
                    const scale = restoreScale()
                    if( myState.current.dragging.dropConfig && myState.current.dragging.dropZone && myState.current.dragging.startZone){
                        if( props.board ){
                            const startFrame = myState.current.dragging.startZone?.findAncestor('.frame')?.attrs.id
                            const dropFrame = myState.current.dragging.dropZone?.findAncestor('.frame')?.attrs.id
                            myState.current.dragging.dropConfig.drop( myState.current.dragging.clone.attrs.id, myState.current.dragging.startZone.attrs.id, myState.current.dragging.dropZone.attrs.id, startFrame, dropFrame)
                        }else{
                            myState.current.dragging.dropConfig.drop( myState.current.dragging.clone.attrs.id, myState.current.dragging.startZone.attrs.id, myState.current.dragging.dropZone.attrs.id )
                        }
                    }
                    if( myState.current.dragging.sourceItem ){

                        myState.current.dragging.sourceItem.visible(true)
                    }
                    if( myState.current.dragging.isFrame ){
                        for( const thisFrame of myState.current.selected.frame){

                            const frameId = thisFrame.attrs.id
                            thisFrame.visible(true)
                            
                            let [px,py] = state.xy
                            px -= memo.x
                            py -= memo.y
                            
                            if( myState.current.dragging.snaps && !state.event.shiftKey){
                                removeAnchorLines()
                                const snaps = myState.current.dragging.snaps
                                let [fx, fy] = convertStageCoordToScene(px - myState.current.dragging.ox, py - myState.current.dragging.oy)
                                const sx = snapTo( fx, snaps.v )
                                const sy = snapTo( fy, snaps.h )
                                let [ox, oy] = convertSceneCoordToScreen(sx, sy)
                                px = ox + myState.current.dragging.ox
                                py = oy + myState.current.dragging.oy
                            }
                            let [fx, fy] = convertStageCoordToScene(px - myState.current.dragging.ox, py - myState.current.dragging.oy)
                            const frame = myState.current.frames.find(d=>d.id === frameId)
                            

                            fx += thisFrame.attrs.x -  myState.current.dragging.minX
                            fy += thisFrame.attrs.y -  myState.current.dragging.minY

                            if( frame ){
                                frame.x = fx
                                frame.y = fy
                                frame.node.setPosition({x:fx, y:fy})
                                
                                if( props.callbacks?.frameMove ){
                                    let fs = frame.scale

                                    const item = myState.current.renderList.find(d=>d?.id === frame.id)
                                    if( item.parentRender ){
                                        let {x:px, y:py, s:ps} = getFramePosition(item.parentRender)
                                        fx = (fx - px) / ps
                                        fy = (fy - py) / ps
                                        fs /= ps
                                    
                                        resizeNestedFrame( item.parentRender )
                                    }
                                    
                                    props.callbacks.frameMove({
                                        id: frameId,
                                        x: fx,
                                        y: fy,
                                        s: fs
                                    })
                                    updateNestedFramePosition( frame.node )
                                }
                            }
                        }
                        stageRef.current.batchDraw()
                        refreshLinks()
                        if( props.enableFrameSelection && myState.current.dragging.frameSelect){
                            console.log(`FINISHED WITH ${myState.current.selected.frame.length} selected frames`)
                            for(const item of myState.current.selected.frame ){
                                createFrameSelect( item )
                            }
                            const frameId = myState.current.dragging.sourceItem.attrs.id
                            if(props.callbacks?.onClick?.frame){
                                props.callbacks.onClick.frame(  [frameId] )
                            }
                        }
                    }
                    destroyDragging()
                    clearHightlights()
                    processHighlights(x,y)
                    if( props.callbacks?.viewportCallback){
                        props.callbacks?.viewportCallback(myState.current.viewport)
                    }
                }
            }
            return memo
        },
        onPinch: (state)=>{
                        //state.event.preventDefault()
                        if( !state.first && myState.current.wasPanning ){
                            myState.current.wasPanning = false
                            alignViewport(myState.current.viewport?.x ?? 0,myState.current.viewport?.y ?? 0, myState.current.viewport?.scale ?? 1, true)
                            return
                        }
                        if( !enableZoom){
                            return
                        }
            
                        let memo = state.memo ?? [0,0, 0, 0,1,false]
                        const ox = state.origin[0]
                        const oy = state.origin[1]

                        if (state.first) {
                            const { width, height, x, y } = frameRef.current.getBoundingClientRect()

                            const tx = ox - x
                            const ty = oy - y
                            memo = [myState.current.viewport?.x ?? 0, myState.current.viewport?.y ?? 0, tx, ty, myState.current.viewport?.scale ?? 1]

                            if( props.callbacks?.viewportWillMove){
                                props.callbacks?.viewportWillMove(myState.current.viewport)
                            }
                        }
                        const oldScale = memo[4]
                        // Clamp per-event scale change so discrete wheel mouse notches
                        // (deltaY ≈ 100-120 → ratio jumps to 0 or max in one event) produce
                        // a smooth ~15% step. Trackpad pinch fires many tiny events (<5% each)
                        // so this clamp never triggers for it.
                        let thisScale = state.offset[0]
                        if (oldScale > 0) {
                            const ratio = thisScale / oldScale
                            thisScale = oldScale * Math.max(0.80, Math.min(1.20, ratio))
                        }

                        const tx = (memo[2] - memo[0]) / oldScale
                        const ty = (memo[3] - memo[1]) / oldScale

                        const x = memo[2] - (tx * thisScale)
                        const y = memo[3] - (ty * thisScale)

                        let updated = alignViewport(x,y, thisScale, state.last)
                        if( state.last ){
                            if( props.callbacks?.viewportCallback){
                                props.callbacks?.viewportCallback(myState.current.viewport)
                            }
                        }

                        if( (myState.current.dragging || myState.current.dragging?.isFrame) && !myState.current.dragging?.sourcePin ){
                            const adjScale = thisScale / myState.current.dragging.startScale 
                            myState.current.dragging.stage.scale({x:adjScale, y:adjScale})
                            myState.current.dragging.ox = myState.current.dragging.sox * adjScale
                            myState.current.dragging.oy = myState.current.dragging.soy * adjScale

                            const dx = myState.current.dragging.spx - myState.current.dragging.ox
                            const dy = myState.current.dragging.spy - myState.current.dragging.oy

                            myState.current.dragging.stage.container().style.transform = `translate(${dx}px, ${dy}px)`;

                            const maxW = myState.current.width + dx
                            const maxH = myState.current.height + dy
                            
                            myState.current.dragging.stage.width( Math.min(myState.current.dragging.sw * adjScale, maxW) )
                            myState.current.dragging.stage.height( Math.min(myState.current.dragging.sh * adjScale, maxH)  )
                            myState.current.dragging.stage.batchDraw()
                        }

                        return [ updated.x, updated.y, memo[2], memo[3],updated.scale, state.pinching] 
                    },
        onWheel: (state) => {
                    if( !state.ctrlKey ){
                        myState.current.wasPanning = true
                      panHandler(state)
                      if( props.events?.wheel?.passive === false){
                          state.event.preventDefault()
                      }

                    }
                }
            },{
                target: frameRef,
                eventOptions: { 
                    passive: true,
                },
                drag:{
                    delay: 350,
                    threshold: [10,10],
                    eventOptions: { 
                        passive: false,
    //                    
                    }
    
                },
                wheel:{
                    eventOptions: { 
                        passive: props.events?.wheel?.passive !== false
                        //preventDefault: true,d
                    }
                },
                pinch: {
                    eventOptions: { 
                        passive: isSafari ? false : true,
    //                    
                    },
                    from: ()=>{
                        let [translateX, translateY, initialScale] = restoreTransform()
                        return [initialScale,initialScale]
                    },
                    //scaleBounds: { min: 0.03, max: 8 },
                    scaleBounds: ()=>{
                        let min = 0.03
                        if( props.board ){
                            let minX, maxX, minY, maxY
                            if( myState.current.frames){
                                myState.current.frames.forEach(frame=>{
                                    let fl = frame.x
                                    let ft = frame.y
                                    let fr = (frame.x + (frame.node.attrs.width * frame.scale))
                                    let fb = (frame.y + (frame.node.attrs.height * frame.scale))


                                    if( minX == undefined || fl < minX){ minX = fl}
                                    if( minY == undefined || ft < minY){ minY = ft}
                                    if( maxX == undefined || fr > maxX){ maxX = fr}
                                    if( maxY == undefined || fb > maxY){ maxY = fb}
                                })
                                min = Math.min( 0.03, myState.current.width / (maxX- minX) / 2,  myState.current.height / (maxY - minY) / 2 )
                            }
                        }
                        let boardScales = []
                        if( props.board && myState.current.frames){
                            boardScales = myState.current.frames.map(frame=>{
                                const subViews = frame.node.find('.view').map(d=>d.attrs.scaleX / 2 ).filter(d=>d)                        
                                const innerScale = subViews.reduce((a,d)=>a < d ? a : d, 1)
                                return (frame.node.attrs.scaleX ?? 1) * innerScale
                            }) 
                        }
                        const max = props.board ? 5 / Math.min(1, ...boardScales) : 8
                        return { min, max }
                    }
                },
            }
        )
        function resolveNodePostion( node ){
            let x = node.x()
            let y = node.y()
            let s = node.scaleX()
            while(node.parent){
                if( node.parent.attrs.name?.includes("frame")){
                    break
                }
                let ps = node.parent.scaleX()
                x *= ps
                y *= ps
                s *= ps
                x += node.parent.x()
                y += node.parent.y()
                node = node.parent
            }
            return {x,y,s}
        }
        function isPointInWedge(pt, wedge) {
            // 1. translate to wedge-centered coords
            const {x: wx, y: wy, s} = resolveNodePostion(wedge)
            const dx = pt.x - wx, dy = pt.y - wy
            // 2. radial check
            if (dx*dx + dy*dy > Math.pow(wedge.radius() * s, 2)) {
                return false;
            }
            // 3. angle check
            let a = (Math.atan2(dy, dx) * 180 / Math.PI + 360) % 360;
            const start = (wedge.rotation() % 360 + 360) % 360;
            const end   = (start + wedge.angle()) % 360;
            if (start < end) {
                return a >= start && a <= end;
            } else {
                // wraps past 360°
                return a >= start || a <= end;
            }
        }

        function findTrackedNodesAtPosition(px,py, classes, includeFrame = false, forClick, forDrag){
            if( !myState.current?.frames){return}
            if( classes ){
                classes = [classes].flat()
            }else{
                classes = []
            }
            let found = []
            let hasFound = false
            const checked = new Set()
            for(const frame of myState.current.frames){
                const clipped = {
                    l: frame.x + (frame.node.attrs.clipX ?? 0),
                    t: frame.y + (frame.node.attrs.clipY ?? 0),
                }
                clipped.r = clipped.l + ((frame.node.attrs.clipWidth === undefined ? frame.node.attrs.width : frame.node.attrs.clipWidth) * frame.scale)
                clipped.b = clipped.t + ((frame.node.attrs.clipHeight === undefined ? frame.node.attrs.height : frame.node.attrs.clipHeight) * frame.scale)

                //const inExapndedFrame = (frame.x - 5 ) <= px && (frame.y - 5) <= py &&  (frame.x + 5 + (frame.node.attrs.width * frame.scale)) >= px && (frame.y + 5 + (frame.node.attrs.height * frame.scale)) >= py
                //const inFrame = inExapndedFrame && (frame.x <= px && frame.y <= py &&  (frame.x + (frame.node.attrs.width * frame.scale)) >= px && (frame.y + (frame.node.attrs.height * frame.scale)) >= py)
                const inExapndedFrame = (clipped.l - 5) <= px && (clipped.t - 5) <= py &&  (clipped.r + 5) >= px && (clipped.b + 5) >= py
                const inFrame = inExapndedFrame && clipped.l <= px && clipped.t <= py &&  clipped.r >= px && clipped.b >= py
                if( inExapndedFrame ){
                    for(const d of frame.lastNodes){
                        let x = px - frame.node.attrs.x
                        let y = py - frame.node.attrs.y
                        x /= frame.scale
                        y /= frame.scale
                        if( !checked.has(d._id) && d.attrs.x <= x && d.attrs.y <= y &&  (d.attrs.x + (d.attrs.width * d.scaleX())) >= x && (d.attrs.y + (d.attrs.height * d.scaleY())) >= y){
                            if( !classes || d.attrs.name?.split(" ").filter(d=>classes.includes(d)).length >0){
                                hasFound = true
                                let addMajor = true

                                if( forClick ){
                                    const clickables = d.nodeType === "Group" ? d.find(".clickable") : (d.name().includes("clickable") ? [d] : [])
                                    if( clickables && clickables?.length){
                                        for( const d2 of clickables ){
                                            const pos = d2.getRelativePointerPosition()
                                            if( d2 instanceof Konva.Wedge ){
                                                if( isPointInWedge( {x, y}, d2)){
                                                    found.push(d2)
                                                    addMajor = false
                                                }

                                            }else{
                                                if( pos.x >= 0 && pos.x <= d2.width() && pos.y >=0 && pos.y <= d2.height()){
                                                    found.push(d2)
                                                    addMajor = false
                                                }
                                            }
                                            /*
                                            if( (d2.attrs.width * myState.current.viewport.scale) > 5 && (d2.attrs.height * myState.current.viewport.scale) > 5){
                                                if( (d.x() + d2.x()) <= x && (d.y() + d2.y()) <= y &&  (d.x() + d2.x() + (d2.attrs.width * d2.scaleX())) >= x && (d.y() + d2.y() + (d2.attrs.height * d2.scaleY())) >= y){
                                                    found.push(d2)
                                                    addMajor = false
                                                }
                                            }*/
                                        }
                                    }
                                }
                                checked.add( d._id)
                                if( addMajor ){
                                    if( !inFrame && !d.attrs.expandedClick){
                                        continue
                                    }
                                    if( props.enableShapeSelection === false){
                                        if( d.attrs.name?.includes("shape_element")){
                                            continue
                                        }
                                    }
                                    if( forClick && d.attrs.name?.includes("no_select")){
                                        continue
                                    }
                                    if( forDrag && d.attrs.name?.includes("no_drag")){
                                        continue
                                    }
                                    found.push(d)
                                }
                            }
                        }
                    }
                    if( props.board && (classes.includes("frame"))){
                        if( hasFound ){
                            if( frame.parentRender ){
                                const parentClicked = found.find(d=>d.attrs.id === frame.parentRender)
                                if( parentClicked ){
                                    found = found.filter(d=>d.attrs.id !== frame.parentRender)
                                    found.push( frame.node )
                                }
                            }

                        }else{
                            found.push(frame.node)
                        }
                    }
                }else{
                    if( (forClick || includeFrame) && frame.label){
                        
                        const lx = frame.label.attrs.x + frame.node.attrs.x
                        const ly= frame.label.attrs.y + frame.node.attrs.y - (frame.label.attrs.height * frame.scale )
                        const inFrameLabel = lx <= px && ly <= py &&  (lx + (frame.label.width() * frame.scale)) >= px && (ly + (frame.label.attrs.height * frame.scale )) >= py
                        if( inFrameLabel){
                            found.push(frame.node)
                        }
                    }
                }
            }
            return found
        }


        function addOverlay( node, label, operation, colors){
            if( node.attrs?.name.includes("widget") || node.attrs?.name.includes("pin")){
                const nodes = node.find ? node.find('.hover_target') : [node]
                for(const d of nodes){
                    if( d && !d.attrs.overlay_label || d.attrs.overlay_label === label ){
                        if( !d.attrs._originalFill ){
                            d.attrs._originalFill = d.attrs.fill
                        }
                        d.attrs.overlay_label = label
                        d.fill( (label !== "select" ? d.attrs?.hoverFill : undefined) ?? colors[operation]?.fill )
                    }
                }
                return
            }
            if( node.attrs?.name.includes("frame") ){
                const existing = node.find('.ring')?.[0]
                if( existing ){
                    existing.stroke(existing.attrs.ringColor ?? "#e1e1e1")
                    existing.strokeWidth( existing.strokeWidth() - 1)
                }
            }

            if( operation === "background"){
                if( node.getClassName() === "Group"){
                    const bg = node.find('Rect')?.[0]
                    if( bg && (!bg.attrs.overlay_label || bg.attrs.overlay_label === "hover" )){
                        bg.attrs.baseFill = bg.attrs.fill
                        bg.attrs.overlay_label = label
                        bg.fill(bg.attrs?.hoverFill ?? colors[operation]?.fill)
                    }
                }
            }
            if( operation === "border"){
                if( node.getClassName() === "Group"){
                    const border = new Konva.Rect({
                        x: 1,
                        y: 1,
                        cornerRadius: 2,
                        width: Math.max(node.attrs.width - 2, 1),
                        height: Math.max(node.attrs.height - 2, 1),
                        stroke: node.attrs?.hoverStroke ?? node.attrs?.hoverFill ?? colors[operation]?.stroke,
                        fill: node.attrs?.hoverFill ?? colors[operation]?.fill,
                        name: label
                    })
                    node.add(border)
                } else {
                    // Non-Group shape (e.g. Rect) — add border to parent, track it on the node
                    const parent = node.getParent();
                    if (parent) {
                        const border = new Konva.Rect({
                            x: node.attrs.x - 1,
                            y: node.attrs.y - 1,
                            width: (node.attrs.width * Math.abs(node.scaleX())) + 2,
                            height: (node.attrs.height * Math.abs(node.scaleY())) + 2,
                            cornerRadius: 2,
                            stroke: node.attrs?.hoverStroke ?? colors[operation]?.stroke ?? "#BA7517",
                            strokeWidth: 1.5,
                            listening: false,
                            name: label
                        })
                        parent.add(border)
                        node._hoverBorder = border
                    }
                }
            }
        }
        function removeOverlay( node, label, operation){
            if( node.attrs?.name.includes("frame") ){
                const existing = node.find('.ring')?.[0]
                if( existing ){
                    existing.stroke( myState.current.background )
                    existing.strokeWidth( existing.strokeWidth() + 1)
                }
            }
            if( node.attrs?.name.includes("widget") || node.attrs?.name.includes("pin")){
                const nodes = node.find ? node.find('.hover_target') : [node]
                for(const d of nodes){
                    if( d && d.attrs.overlay_label === label){
                        if( d.attrs._originalFill){
                            d.fill( d.attrs._originalFill)
                            d._originalFill = undefined
                            d.attrs.overlay_label = undefined
                        }
                    }
                }
                return
            }
            if( operation === "background"){
                if( node.getClassName() === "Group"){
                    const bg = node.find('Rect')?.[0]
                    if( bg && bg.attrs.overlay_label === label){
                        bg.fill(bg.attrs.baseFill)
                        bg.attrs.overlay_label = undefined
                    }
                }
            }
            if( operation === "border"){
                if( node._hoverBorder ){
                    node._hoverBorder.remove()
                    node._hoverBorder = null
                } else if( node.getClassName() === "Group"){
                    const border = node.find(`.${label}`)?.[0]
                    if( border ) border.remove()
                }
            }
        }

        function leftNode(node, type){
            const operation = props.highlights?.[type]
            removeOverlay( node, "border", operation)
        }
        function enteredNode(node, type, prefix){
            const operation = props.highlights?.[type]
            addOverlay( node, "border", operation, colors[prefix + "hover"])
        }

        function processMouseMove(e){
            if( myState.current.frameSelect?.transforming){return}
            if( props.enableTextSelection && myState.current.textSelection?.dragging ){
                updateTextSelectionFromEvent(e)
                frameRef.current.style.cursor = "text"
                myState.current.cursor = "text"
                return
            }
            let cursor = ""            
            let [x, y] = convertEventToScene(e)
            if( !props.disableHover ){
                if( processHighlights(x,y) ){
                    cursor = cursor || "pointer"
                }
            }
            if( !cursor ){
                cursor = "default"
            }

            if( cursor !== myState.current.cursor){
                frameRef.current.style.cursor = cursor
                myState.current.cursor = cursor
            }
        }
        function clearSelection(skip){
            myState.current.hover ||= {}
            for(const type of Object.keys(props.selectable ?? {})){
                if( skip && skip.includes(type)){
                    continue
                }
                if( myState.current?.selected?.[type] ){
                    for( const d of myState.current.selected[type]){
                        removeOverlay(d, "select", props.highlights?.[type] ?? "border")
                    }
                    myState.current.selected[type] = undefined
                }
            }
            stageRef.current.batchDraw()
        }
        function clearHightlight(type){
            if( myState.current.hover?.[type] ){
                let n = myState.current.hover[type]
                leftNode(n, type)
                myState.current.hover[type] = undefined

                let canvas = layerRef.current.getCanvas()
                n.drawScene( canvas )
            }
        }
        function clearHightlights(){
            myState.current.hover ||= {}
            for(const type of Object.keys(props.highlights ?? {})){
                if( myState.current.hover[type] ){
                    leftNode(myState.current.hover[type], type)
                    myState.current.hover[type] = undefined
                }
            }
        }
        function doHighlight(found, type, prefix = ""){
            let doDraw = false
            let cleared 
            if( found ){
                if( found.name().includes("no_hover")){return}
                const thisItem = found
                if( myState.current.hover[type] !== thisItem ){
                    if( myState.current.hover[type] ){
                        doDraw = true
                        cleared = myState.current.hover[type]
                        leftNode(myState.current.hover[type], type, prefix)
                    }
                    myState.current.hover[type] = thisItem
                    if(myState.current.hover[type]){
                        doDraw = true
                        enteredNode(myState.current.hover[type], type, prefix)
                    }
                }
            }else{
                if( myState.current.hover[type] ){
                    doDraw = true
                    leftNode(myState.current.hover[type], type, prefix)
                    cleared = myState.current.hover[type]
                    myState.current.hover[type] = undefined
                }
            }
            if( doDraw ){
                let updates = []
                    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
                    
                    for(const d of [cleared, found]){
                        if(!d){continue}
                        let x1, y1,frame
                        if( d.name() === "frame"){
                          frame = d   
                            x1 = frame.attrs.x
                            y1 = frame.attrs.y
                        }else{
                            frame =  d.findAncestor('.frame')
                            x1 = d.attrs.x + frame.attrs.x
                            y1 = d.attrs.y + frame.attrs.y
                        }
                        const x2 = x1 + frame.attrs.width, y2 = y1 + frame.attrs.height
                        if( x1 < minX){minX = x1}
                        if( y1 < minY){minY = y1}
                        if( x2 > maxX){maxX = x2}
                        if( y2 > maxY){maxY = y2}
                        
                    }
                    let count = 0, changed = false
                    const seen = new Set();
                    for(const frame of myState.current.frames){
                        const forFrame = []
                        do{
                            changed = false
                            for(const d of frame.lastNodes){
                                if( seen.has(d._id) ){
                                    continue
                                }

                                const clip = d.clip ? d.clip() : false
                                let width = clip.width !== undefined ? Math.min(clip.width, d.attrs.width) : d.attrs.width
                                let height = clip.height !== undefined ? Math.min(clip.height,d.attrs.height) : d.attrs.height

                                let x1 = d.attrs.x + frame.x, y1 = d.attrs.y + frame.y
                                let x2 = x1 + width, y2 = y1 + height  
                                if(x2 >= minX &&  x1 <= maxX && y2 >= minY && y1 <= maxY){
                                    if(d.attrs.name === "frame"  || d.attrs.name === "view" ){
                                        continue
                                    }
                                    forFrame.push(d)
                                    seen.add(d._id)
                                    changed = true
                                    count++
                                    if( x1 < minX){minX = x1}
                                    if( y1 < minY){minY = y1}
                                    if( x2 > maxX){maxX = x2}
                                    if( y2 > maxY){maxY = y2}
                                    continue
                                }
                            }
                        }while(changed)
                        updates.push(...forFrame.sort((a,b)=>a.zIndex() - b.zIndex()))
                    }
                requestAnimationFrame(()=>{
                    let canvas = layerRef.current.getCanvas()
                    for(const d of updates){
                        d.drawScene(canvas)
                    }
                })
            }            
        }
        function processHighlights(x,y, dropCandidate){
            if( !dropCandidate && myState.current.dragging){
                return 
            }

            myState.current.hover ||= {}
            let anythingFound = false
            for(const type of Object.keys(props.highlights ?? {})){
                let found = orderInteractiveNodes(findTrackedNodesAtPosition( x, y, type))?.[0]
                anythingFound ||= found
                doHighlight( found, type)

            }
            return anythingFound
        }
        function orderInteractiveNodes(found = []) {
            // 1) Enrich each node with the metadata we need
            const enriched = found.map(node => {
                const absZ      = node.getAbsoluteZIndex();
                const frame     = node.findAncestor('.frame');
                const frameZ    = frame ? frame.getAbsoluteZIndex() : 0;
                const name      = node.name() || '';
                const isClick   = /\bclickable\b/.test(name);
                let   infTrackZ = 0;

                if (isClick) {
                    const infTrack = node.findAncestor(n => {
                        const nm = n.name() || '';
                        return nm.includes('inf_track');
                    });
                    infTrackZ = infTrack ? infTrack.getAbsoluteZIndex() : 0;
                }

                return { node, absZ, frameZ, isClick, infTrackZ };
            });

            // 2) Sort with our custom rules
            enriched.sort((a, b) => {
                // A) Different frames? higher‐z frame wins.
                if (a.frameZ !== b.frameZ) {
                    return b.frameZ - a.frameZ;
                }

                // B) Same frame & both clickables? bucket by inf_track, then by z.
                if (a.isClick && b.isClick) {
                    if (a.infTrackZ !== b.infTrackZ) {
                        return b.infTrackZ - a.infTrackZ;
                    }
                    return b.absZ - a.absZ;
                }

                // C) Otherwise (non‐clickables or mixed), just by global z‐order.
                return b.absZ - a.absZ;
            });

            // 3) Strip off our metadata and return just the nodes in draw order
            return enriched.map(e => e.node);
        }
        async function processClick(e){
            if( myState.current.ignoreClick ){
                myState.current.ignoreClick = false
                return
            }
            if( props.enableTextSelection && myState.current.textSelection?.dragging ){
                myState.current.ignoreClick = true
                return
            }
            let [x, y] = convertEventToScene(e)
            const clickable_names = ["widget", "inf_track", "frame_label",...Object.keys(props.selectable ?? {}), ...Object.keys(props.callbacks?.onClick ?? {})].filter((d,i,a)=>a.indexOf(d) === i)
            if( clickable_names.length === 0 ){
                return
            }
            let found = orderInteractiveNodes(findTrackedNodesAtPosition( x, y, clickable_names, undefined, true))
            if( found.length > 1 ){
                const nonBackground = found.filter(node=>{
                    const name = node?.attrs?.name ?? ""
                    const id = node?.attrs?.id
                    return !(name.includes("background") && name.includes("clickable")) && id !== "background"
                })
                if( nonBackground.length > 0 ){
                    found = nonBackground
                }
            }
            //console.log(found)

            let clickedFrames = new Set()
            let doneClick = false
            for( const d of found){
                if( d.attrs.onClick ){
                    d.attrs.onClick(d.attrs.id)
                    doneClick = true
                }else{
                    const clsNames = d.attrs.name?.split(" ") ?? []
                    for(const cls of clsNames){
                        if( props.selectable?.[cls]){

                            if( myState.current.lastSelection && myState.current.lastSelection !== cls && props.selectable[myState.current.lastSelection].multiple && e.evt.shiftKey ){
                                continue
                            }

                            if( !myState.current.lastSelection  || 
                                props.selectable[cls].multiple !== true ||
                                cls !== myState.current.lastSelection 
                                || e.evt.shiftKey == false
                                ){
                                clearSelection()
                                if( props.enableFrameSelection && cls === "frame" ){
                                    destroyFrameSelect()
                                }
                            }
                            myState.current.selected ||= {}
                            myState.current.lastSelection = cls
                            myState.current.selected[cls] ||= []
                            myState.current.selected[cls].push(d)
                            
                            leftNode(d, cls)
                            if( props.enableFrameSelection && cls === "frame" ){
                                createFrameSelect( d )
                                console.log(myState.current.selected.frame.map(d=>d.attrs.id))
                            }else{
                                addOverlay(d, "select", props.highlights?.[cls] ?? "border", colors.select)
                            }
                            doneClick = true
                        }                        
                        if( cls === "widget"){
                            const frameId = d.findAncestor('.frame')?.attrs.id
                            const triggerNames = clsNames.filter(d=>!["widget" ,"inf_track"].includes(d))
                            for(const trigger of triggerNames){
                                if( props.callbacks?.onClick?.widget?.[trigger]){
                                    props.callbacks?.onClick?.widget?.[trigger](d, frameId)
                                }

                            }
                        }else if( cls === "frame_label"){
                            const frame = d.findAncestor('.frame') 
                            if(frame){
                                const result = props.callbacks?.onClick?.["frame"]( [frame.attrs.id] )
                                doneClick = true

                            }

                        }else if( props.callbacks?.onClick?.[cls] ){
                            //const frameId = myState.current.selected[cls][0].findAncestor('.frame')?.attrs.id
                            //const ids = myState.current.selected[cls].map(d=>d.attrs.id)
                            
                            const frameId = d.findAncestor('.frame')?.attrs.id
                            const ids = [d,myState.current.selected?.[cls]].flatMap(d=>d?.attrs?.id).filter((d,i,a)=>d && a.indexOf(d) === i) 

                            const result = props.callbacks?.onClick?.[cls]( ids.length > 1 ? ids : ids, frameId, d.attrs.data, d)
                            if(result !== false){
                                doneClick = true
                            }
                        }else{
                            const frameId = d.findAncestor('.frame')?.attrs.id
                            if( frameId ){
                                clickedFrames.add(frameId)
                            }
                        }
                    }
                }
                if(doneClick){
                    break
                }
            }
            if(!doneClick){
                clearSelection()
                destroyFrameSelect()                
                if( props.callbacks?.onClick?.frame ){
                    props.callbacks.onClick.frame(Array.from(clickedFrames)[0])

                }
                if( props.callbacks?.onClick?.canvas ){
                    props.callbacks.onClick.canvas()

                }
            }
            e.evt.stopPropagation()
        }
        function processMouseDown(e){
            if( !props.enableTextSelection ){
                return
            }
            const [x, y] = convertEventToScene(e)
            const textNode = findCustomTextAtPosition(x, y)
            if( textNode ){
                return
            }
            clearTextSelection()
        }
        function processMouseUp(e){
            if( !props.enableTextSelection ){
                return
            }
            if( myState.current.textSelection?.dragging ){
                updateTextSelectionFromEvent(e)
                myState.current.textSelection.dragging = false
                if( myState.current.textSelection?.moved ){
                    myState.current.ignoreClick = true
                }
            }
        }
        function snapTo( v, snaps, threshold = 10){
            const minDist = snaps.reduce((a,c)=>{
                const dist = Math.sqrt(Math.pow(v - c, 2));
                if( dist < a.d){
                    a.d = dist
                    a.v = c
                }
                return a
            },{d: Infinity})
            if (minDist.v !== undefined && minDist.d < threshold) {
                return minDist.v
            }
            return v
        }
        function showAnchorLines( parent, px, py, show, sx, sy, snaps, bounds ){
            const x = px// - parent.attrs.x
            const y = py// - parent.attrs.y

            function getExtremes(scoped, boundsMin, boundsMax){
                const thisFrame = scoped.flatMap(d=>d[3] ? [boundsMin, boundsMax, d[0], d[1]] : undefined).filter(d=>d)
                const otherFrame = scoped.flatMap(d=>d[3] ? undefined : [d[0], d[1]]).filter(d=>d)

                return {
                    thisFrame: thisFrame.length > 0 ? {
                        min: thisFrame.reduce((a,c)=>c < a ? c : a, Infinity),
                        max: thisFrame.reduce((a,c)=>c > a ? c : a, -Infinity)
                    } : undefined,
                    otherFrame: otherFrame.length > 0 ? {
                        min: otherFrame.reduce((a,c)=>c < a ? c : a, Infinity),
                        max: otherFrame.reduce((a,c)=>c > a ? c : a, -Infinity),
                        anchor: scoped[0][2]                        
                    } : undefined
                }
            }
            function withArrow( min, max, o, anchor, mode = "v"){
                const mid = (min + max) /2
                const angle = Math.abs(Math.min(Math.max((max-min) * 0.2, (o - anchor) * 0.15), 20))

                const angleX = mode ==="v" ? o > anchor ? angle : -angle : angle
                const angleY = mode ==="v" ? angle : o > anchor ? angle : -angle
                
                if( mode === "v"){
                    return `M${o} ${min} L${o} ${max} M${o} ${(mid)} L${anchor} ${(mid)} M${o} ${(mid)} l${-angleX} ${-angleY} M${o} ${(mid)} l${-angleX} ${angleY} M${anchor} ${(mid)} l${angleX} ${-angleY} M${anchor} ${(mid)} l${angleX} ${angleY}`
                }else{
                    return `M${min} ${o} L${max} ${o} M${mid} ${o} L${mid} ${anchor} M${mid} ${o} l${-angleX} ${-angleY} M${mid} ${o} l${angleX} ${-angleY} M${mid} ${anchor} l${-angleX} ${angleY} M${mid} ${anchor} l${angleX} ${angleY}`
                }
            }

            for(const d of parent.children){
                if( d.attrs.name === "v"){
                    d.visible( show != "h" && Math.abs(x - d.attrs.rc) < 50 )
                }else if( d.attrs.name === "h"){
                    d.visible(show != "v" && Math.abs(y - d.attrs.rc) < 50 )
                }else if( d.attrs.name === "active_v"){
                    let shown = false
                    if( sx !== px){
                        const scope1 = snaps.segments.v[sx]
                        if( scope1){
                            let points = ""
                            for(const ox of Object.keys(scope1)){
                                const scoped = scope1[ox]
                                const extremes = getExtremes(scoped, bounds[1], bounds[3])
                                if( extremes.thisFrame){
                                    points += `M${ox} ${extremes.thisFrame.min} L${ox} ${extremes.thisFrame.max} `
                                }
                                if( extremes.otherFrame){
                                    points += withArrow( extremes.otherFrame.min, extremes.otherFrame.max, ox, extremes.otherFrame.anchor)
                                    points += withArrow( bounds[1], bounds[3], sx, bounds[0])
                                }
                                shown = true
                            }
                            d.data(points)
                        }
                    }
                    d.visible(shown)
                }else if( d.attrs.name === "active_h"){
                    let shown = false
                    if( sy !== py){
                        const scope1 = snaps.segments.h[sy]
                        if( scope1){
                            let points = ""
                            for(const oy of Object.keys(scope1)){
                                const scoped = scope1[oy]
                                const extremes = getExtremes(scoped, bounds[0], bounds[2])
                                if( extremes.thisFrame){
                                    points += `M${extremes.thisFrame.min} ${oy} L${extremes.thisFrame.max} ${oy} `
                                }
                                if( extremes.otherFrame){
                                    points += withArrow( extremes.otherFrame.min, extremes.otherFrame.max, oy, extremes.otherFrame.anchor, "h")
                                    points += withArrow( bounds[0], bounds[2], sy, bounds[1], "h")
                                }
                                shown = true
                            }
                            d.data(points)
                        }
                    }
                    d.visible(shown)
                }
            }
            layerRef.current.batchDraw()
        }
        function removeAnchorLines( ){
            layerRef.current.find('#anchors').forEach((existing)=>{
                existing.destroy()
            })
        }

        function renderAnchorLines( snaps, frame ){
            const p = framePosition(frame)
            removeAnchorLines()
            const g = new Konva.Group( {
                id: "anchors",

            })
            for( const a of ["h","v"]){
                for( const d of Object.keys(snaps.segments[a])){
                    for(const d2 of Object.keys(snaps.segments[a][d])){
                        for(const s of snaps.segments[a][d][d2]){
                            g.add(new Konva.Line({
                                //visible: false, 
                                stroke: "#4dbeff", 
                                dash: [8,3],
                                rc: parseFloat(d),
                                strokeWidth: 1, 
                                strokeScaleEnabled: false, 
                                points:a === "v" ? [parseFloat(d2), s[0], parseFloat(d2), s[1]] : [s[0], parseFloat(d2), s[1], parseFloat(d2)],
                                name: a}))
                        }
                    }
                }
            }
            g.add(new Konva.Path({
                visible: false, 
                stroke: "#4dbeff", 
                strokeWidth: 1, 
                strokeScaleEnabled: false, 
                name: "active_v"}))
            layerRef.current.add( g )
            g.add(new Konva.Path({
                visible: false, 
                stroke: "#4dbeff", 
                strokeWidth: 1, 
                strokeScaleEnabled: false, 
                name: "active_h"}))
            layerRef.current.add( g )

            g.add(new Konva.Rect({
                x: myState.current.userView.x,// + (myState.current.userView.width * 0.1),
                y: myState.current.userView.y,// + (myState.current.userView.height * 0.1),
                width: myState.current.userView.width ,
                height: myState.current.userView.height,
                stroke: "red", 
                strokeWidth: 1, 
                strokeScaleEnabled: false, 
                }))
            layerRef.current.add( g )

            layerRef.current.batchDraw()
            return g
        }


        function addAnchorToSet( set, d, o, bounds){
            if( !set[d] ){
                set[d] = {}
            }
            if( !set[d][o]){
                set[d][o] = []
            }
            set[d][o].push(bounds)
        }

        function getAnchorLinesForFrame( frame, skipId = undefined, withBounds, v, h ){
            const pPosition = framePosition(frame)
            for(const d of getNestedFrames(frame)){
                if( d.id === skipId){
                    continue
                }
                const childPosition = framePosition(d.id)
                
                addAnchorToSet( v, withBounds ? childPosition.scene.l - pPosition.scene.l + withBounds[0] : childPosition.scene.l, childPosition.scene.l, [childPosition.scene.t, childPosition.scene.b, pPosition.scene.l, withBounds === undefined])
                addAnchorToSet( v, withBounds ? childPosition.scene.r - pPosition.scene.l + withBounds[0] : childPosition.scene.r, childPosition.scene.r, [childPosition.scene.t, childPosition.scene.b, pPosition.scene.l, withBounds === undefined])
                addAnchorToSet( h, withBounds ? childPosition.scene.t - pPosition.scene.t + withBounds[1] : childPosition.scene.t, childPosition.scene.t, [childPosition.scene.l, childPosition.scene.r, pPosition.scene.t, withBounds === undefined])
                addAnchorToSet( h, withBounds ? childPosition.scene.b - pPosition.scene.t + withBounds[1] : childPosition.scene.b, childPosition.scene.b, [childPosition.scene.l, childPosition.scene.r, pPosition.scene.t, withBounds === undefined])
            }
        }
        function getTextMeasureContext(){
            if( textMeasureCtxRef.current ){
                return textMeasureCtxRef.current
            }
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            if( ctx ){
                ctx.textBaseline = "middle"
            }
            textMeasureCtxRef.current = ctx
            return ctx
        }
        function ensureTextSelectionGroup(){
            const selectionLayer = lineLayerRef.current ?? layerRef.current
            if( !selectionLayer ){
                return null
            }
            let group = selectionLayer.findOne('#text_selection')
            if( !group ){
                group = new Konva.Group({ id: 'text_selection', listening: false })
                selectionLayer.add(group)
            }
            group.moveToTop?.()
            return group
        }
        function clearTextSelection(){
            const group = (lineLayerRef.current ?? layerRef.current)?.findOne('#text_selection')
            if( group ){
                group.destroyChildren()
            }
            myState.current.textSelection = undefined
            lineLayerRef.current?.batchDraw()
            layerRef.current?.batchDraw()
        }
        function findCustomTextAtPosition(px, py){
            const frames = orderInteractiveNodes(findTrackedNodesAtPosition(px, py, "frame", true)) ?? []
            for( const frameNode of frames ){
                if( !frameNode?.find ){
                    continue
                }
                const frameItem = myState.current.frames?.find(d2=>d2.id === frameNode.attrs.id || d2.node === frameNode)
                if( !frameItem ){
                    continue
                }
                const textNodes = frameNode.find('CustomText') ?? []
                for( const textNode of textNodes ){
                    const localPos = resolveNodePostion(textNode)
                    const width = textNode.width?.() ?? textNode.attrs?.width
                    const height = textNode.height?.() ?? textNode.attrs?.height
                    const sceneX = frameItem.x + (localPos.x * frameItem.scale)
                    const sceneY = frameItem.y + (localPos.y * frameItem.scale)
                    const sceneScale = (localPos.s ?? 1) * frameItem.scale
                    const sceneW = (width ?? 0) * sceneScale
                    const sceneH = (height ?? 0) * sceneScale
                    if(
                        width != null &&
                        height != null &&
                        px >= sceneX &&
                        py >= sceneY &&
                        px <= (sceneX + sceneW) &&
                        py <= (sceneY + sceneH)
                    ){
                        return textNode
                    }
                }
            }
            return null
        }
        function applyFontScale(font, scale){
            if( !font || !scale || scale === 1 ){
                return font
            }
            const match = font.match(/(\d+(?:\.\d+)?)px/)
            if( !match ){
                return font
            }
            const size = parseFloat(match[1])
            const next = Math.max(size * scale, 0.1)
            return font.replace(match[0], `${next}px`)
        }
        function getLineFont(textNode, line){
            if( textNode?._fontForFlags ){
                const base = textNode._fontForFlags({
                    bold: Boolean(line?.bold),
                    italic: Boolean(line?.italic),
                    large: Boolean(line?.large)
                })
                return applyFontScale(base, line?.fontScale)
            }
            return textNode?.fontCache ?? textNode?.fontStyle?.() ?? undefined
        }
        function getLineLayout(textNode, line, lineIndex){
            const padding = textNode.padding?.() ?? 0
            const fontSize = textNode.fontSize?.() ?? 12
            const lineHeightPx = (textNode.lineHeight?.() ?? 1) * fontSize * (line?.fontScale ?? 1)
            const baseY = line?.y ?? (padding + (lineHeightPx / 2) + (lineIndex * lineHeightPx))
            const width = line?.width ?? 0
            const align = textNode.align?.() ?? "left"
            let offset = padding + (line?.indent ?? 0)
            if( align === "center" ){
                offset += (textNode.width?.() - width) / 2
            }else if( align === "right" ){
                offset += (textNode.width?.() - width)
            }
            return { lineHeightPx, lineY: baseY, offsetX: offset, lineWidth: width }
        }
        function getTextNodeFrameItem(textNode){
            const frameNode = textNode?.findAncestor?.('.frame')
            if( !frameNode ){
                return null
            }
            return myState.current.frames?.find(d2=>d2.id === frameNode.attrs.id || d2.node === frameNode) ?? null
        }
        function getTextNodeSceneTransform(textNode){
            const frameItem = getTextNodeFrameItem(textNode)
            if( !frameItem ){
                return null
            }
            const localPos = resolveNodePostion(textNode)
            const localScale = localPos.s ?? 1
            return {
                frameItem,
                localPos,
                sceneX: frameItem.x + (localPos.x * frameItem.scale),
                sceneY: frameItem.y + (localPos.y * frameItem.scale),
                sceneScale: localScale * frameItem.scale,
            }
        }
        function getLocalPointFromEvent(textNode, eventLike){
            const [stageX, stageY] = getEventStagePosition(eventLike)
            const [sceneX, sceneY] = convertStageCoordToScene(stageX, stageY)
            const transform = getTextNodeSceneTransform(textNode)
            if( !transform ){
                return { x: 0, y: 0 }
            }
            const frameLocalX = (sceneX - transform.frameItem.x) / transform.frameItem.scale
            const frameLocalY = (sceneY - transform.frameItem.y) / transform.frameItem.scale
            const localX = (frameLocalX - transform.localPos.x) / (transform.localPos.s ?? 1)
            const localY = (frameLocalY - transform.localPos.y) / (transform.localPos.s ?? 1)
            return { x: localX, y: localY }
        }
        function getLocalPointFromSceneCoords(textNode, sceneX, sceneY){
            const transform = getTextNodeSceneTransform(textNode)
            if( !transform ){
                return { x: 0, y: 0 }
            }
            const frameLocalX = (sceneX - transform.frameItem.x) / transform.frameItem.scale
            const frameLocalY = (sceneY - transform.frameItem.y) / transform.frameItem.scale
            const localX = (frameLocalX - transform.localPos.x) / (transform.localPos.s ?? 1)
            const localY = (frameLocalY - transform.localPos.y) / (transform.localPos.s ?? 1)
            return { x: localX, y: localY }
        }
        function getCharIndexForLine(textNode, line, localX, lineIndex){
            const ctx = getTextMeasureContext()
            if( !ctx ){
                return 0
            }
            const font = getLineFont(textNode, line)
            if( font ){
                ctx.font = font
            }
            const { offsetX } = getLineLayout(textNode, line, lineIndex)
            const text = line?.text ?? ""
            const targetX = Math.max(localX - offsetX, 0)
            if( text.length === 0 ){
                return 0
            }
            let lo = 0
            let hi = text.length
            while( lo < hi ){
                const mid = Math.floor((lo + hi) / 2)
                const width = ctx.measureText(text.slice(0, mid + 1)).width
                if( width >= targetX ){
                    hi = mid
                }else{
                    lo = mid + 1
                }
            }
            return Math.min(lo, text.length)
        }
        function getSelectionIndex(textNode, localX, localY){
            const lines = textNode.textArr ?? []
            if( lines.length === 0 ){
                return { lineIndex: 0, charIndex: 0 }
            }
            let lineIndex = 0
            for( let i = 0; i < lines.length; i++ ){
                const { lineHeightPx, lineY } = getLineLayout(textNode, lines[i], i)
                const top = lineY - (lineHeightPx / 2)
                const bottom = top + lineHeightPx
                if( localY >= top && localY <= bottom ){
                    lineIndex = i
                    break
                }
                if( localY > bottom ){
                    lineIndex = i
                }
            }
            const charIndex = getCharIndexForLine(textNode, lines[lineIndex], localX, lineIndex)
            return { lineIndex, charIndex }
        }
        function findAncestorWithRenderContext(node){
            let current = node
            while( current ){
                if( current.attrs?.renderContextId ){
                    return current
                }
                current = current.parent
            }
            return null
        }
        function getSelectionScopeNode(node){
            return (
                findAncestorWithRenderContext(node) ??
                node?.findAncestor?.('.view') ??
                node?.findAncestor?.('.primitive') ??
                node?.findAncestor?.('.frame')
            )
        }
        function getFrameTextNodes(scopeNode){
            if( !scopeNode?.find ){
                return []
            }
            const nodes = scopeNode.find('CustomText') ?? []
            return nodes
                .map(node=>({ node, pos: resolveNodePostion(node) }))
                .sort((a,b)=>{
                    if( a.pos.y !== b.pos.y ){
                        return a.pos.y - b.pos.y
                    }
                    return a.pos.x - b.pos.x
                })
                .map(entry=>entry.node)
        }
        function findClosestTextNodeByY(scopeNode, sceneY){
            const nodes = getFrameTextNodes(scopeNode)
            let best = null
            let bestDist = Infinity
            for( const node of nodes ){
                const transform = getTextNodeSceneTransform(node)
                if( !transform ){
                    continue
                }
                const height = node.height?.() ?? node.attrs?.height
                const top = transform.sceneY
                const bottom = top + ((height ?? 0) * transform.sceneScale)
                const clampedY = Math.min(Math.max(sceneY, top), bottom)
                const dist = Math.abs(sceneY - clampedY)
                if( dist < bestDist ){
                    bestDist = dist
                    best = node
                }
            }
            return best
        }
        function getNodeEndIndex(node){
            const lines = node?.textArr ?? []
            if( lines.length === 0 ){
                return { lineIndex: 0, charIndex: 0 }
            }
            const lastLineIndex = lines.length - 1
            const lastText = lines[lastLineIndex]?.text ?? ""
            return { lineIndex: lastLineIndex, charIndex: lastText.length }
        }
        function renderSelectionForNode(node, startIndex, endIndex, selectionGroup, ctx){
            const lines = node.textArr ?? []
            if( lines.length === 0 ){
                return []
            }
            const transform = getTextNodeSceneTransform(node)
            if( !transform ){
                return []
            }
            const startBeforeEnd = startIndex.lineIndex < endIndex.lineIndex || (startIndex.lineIndex === endIndex.lineIndex && startIndex.charIndex <= endIndex.charIndex)
            const start = startBeforeEnd ? startIndex : endIndex
            const end = startBeforeEnd ? endIndex : startIndex
            let selectedText = []
            for( let i = start.lineIndex; i <= end.lineIndex; i++ ){
                const line = lines[i]
                if( !line ){
                    continue
                }
                const font = getLineFont(node, line)
                if( font ){
                    ctx.font = font
                }
                const { lineHeightPx, lineY, offsetX } = getLineLayout(node, line, i)
                const text = line.text ?? ""
                const lineStart = i === start.lineIndex ? start.charIndex : 0
                const lineEnd = i === end.lineIndex ? end.charIndex : text.length
                if( lineEnd < lineStart ){
                    continue
                }
                const beforeStart = text.slice(0, lineStart)
                const beforeEnd = text.slice(0, lineEnd)
                const startX = offsetX + (beforeStart.length ? ctx.measureText(beforeStart).width : 0)
                const endX = offsetX + (beforeEnd.length ? ctx.measureText(beforeEnd).width : 0)
                const leftX = Math.min(startX, endX)
                const rightX = Math.max(startX, endX)
                const top = lineY - (lineHeightPx / 2)
                const rectX = transform.sceneX + (leftX * transform.sceneScale)
                const rectY = transform.sceneY + (top * transform.sceneScale)
                const rectW = Math.max((rightX - leftX) * transform.sceneScale, 1)
                const rectH = Math.max(lineHeightPx * transform.sceneScale, 1)
                const rect = new Konva.Rect({
                    x: rectX,
                    y: rectY,
                    width: rectW,
                    height: rectH,
                    fill: "rgba(59,130,246,0.25)",
                    listening: false,
                })
                selectionGroup.add(rect)
                selectedText.push(text.slice(lineStart, lineEnd))
            }
            return selectedText
        }
        function compareNodePositions(a, b){
            const posA = resolveNodePostion(a)
            const posB = resolveNodePostion(b)
            if( posA.y !== posB.y ){
                return posA.y - posB.y
            }
            return posA.x - posB.x
        }
        function updateTextSelection(textNode, startIndex, endIndex){
            if( !textNode ){
                return
            }
            const selectionGroup = ensureTextSelectionGroup()
            if( !selectionGroup ){
                return
            }
            selectionGroup.destroyChildren()
            const ctx = getTextMeasureContext()
            if( !ctx ){
                return
            }
            const selection = myState.current.textSelection ?? {}
            const anchorNode = selection.anchorNode ?? textNode
            const focusNode = selection.focusNode ?? textNode
            const scopeNode = selection.scopeNode ?? getSelectionScopeNode(anchorNode)
            const frameTextNodes = getFrameTextNodes(scopeNode)
            const anchorIdx = frameTextNodes.indexOf(anchorNode)
            const focusIdx = frameTextNodes.indexOf(focusNode)
            const startNodeIndex = anchorIdx !== -1 && focusIdx !== -1 ? Math.min(anchorIdx, focusIdx) : anchorIdx !== -1 ? anchorIdx : focusIdx
            const endNodeIndex = anchorIdx !== -1 && focusIdx !== -1 ? Math.max(anchorIdx, focusIdx) : startNodeIndex
            const directionUp = anchorIdx !== -1 && focusIdx !== -1
                ? focusIdx < anchorIdx
                : compareNodePositions(focusNode, anchorNode) < 0
            let selectedText = []
            if( startNodeIndex === -1 ){
                selectedText = renderSelectionForNode(textNode, startIndex, endIndex, selectionGroup, ctx)
            }else{
                for( let nodeIdx = startNodeIndex; nodeIdx <= endNodeIndex; nodeIdx++ ){
                    const node = frameTextNodes[nodeIdx]
                    if( !node ){
                        continue
                    }
                    let nodeStart = { lineIndex: 0, charIndex: 0 }
                    let nodeEnd = getNodeEndIndex(node)
                    if( anchorNode === focusNode ){
                        nodeStart = startIndex
                        nodeEnd = endIndex
                    }else if( directionUp ){
                        if( node === focusNode ){
                            nodeStart = endIndex
                            nodeEnd = getNodeEndIndex(node)
                        }else if( node === anchorNode ){
                            nodeStart = { lineIndex: 0, charIndex: 0 }
                            nodeEnd = startIndex
                        }
                    }else{
                        if( node === anchorNode ){
                            nodeStart = startIndex
                            nodeEnd = getNodeEndIndex(node)
                        }else if( node === focusNode ){
                            nodeStart = { lineIndex: 0, charIndex: 0 }
                            nodeEnd = endIndex
                        }
                    }
                    selectedText.push(...renderSelectionForNode(node, nodeStart, nodeEnd, selectionGroup, ctx))
                    if( nodeIdx < endNodeIndex ){
                        selectedText.push("")
                    }
                }
            }
            myState.current.textSelection = {
                node: textNode,
                anchor: myState.current.textSelection?.anchor ?? startIndex,
                anchorNode,
                focusNode,
                scopeNode,
                start: startIndex,
                end: endIndex,
                text: selectedText.join("\n"),
                dragging: myState.current.textSelection?.dragging ?? false,
            }
            lineLayerRef.current?.batchDraw()
            layerRef.current?.batchDraw()
        }
        function startTextSelection(textNode, eventLike){
            const local = getLocalPointFromEvent(textNode, eventLike)
            const startIndex = getSelectionIndex(textNode, local.x, local.y)
            const [startStageX, startStageY] = getEventStagePosition(eventLike)
            updateTextSelection(textNode, startIndex, startIndex)
            const scopeNode = getSelectionScopeNode(textNode)
            myState.current.textSelection = {
                node: textNode,
                anchor: startIndex,
                anchorNode: textNode,
                focusNode: textNode,
                scopeNode,
                start: startIndex,
                end: startIndex,
                dragging: true,
                moved: false,
                startStageX,
                startStageY,
                text: "",
            }
        }
        function updateTextSelectionFromEvent(eventLike){
            const selection = myState.current.textSelection
            if( !selection?.node ){
                return
            }
            const [stageX, stageY] = getEventStagePosition(eventLike)
            const [sceneX, sceneY] = convertStageCoordToScene(stageX, stageY)
            const hoverNode = findCustomTextAtPosition(sceneX, sceneY)
            const sameScope = selection.scopeNode && hoverNode && getSelectionScopeNode(hoverNode) === selection.scopeNode
            let focusNode = sameScope ? hoverNode : selection.node
            if( !hoverNode && selection.scopeNode ){
                const closest = findClosestTextNodeByY(selection.scopeNode, sceneY)
                if( closest ){
                    focusNode = closest
                }
            }
            const local = hoverNode
                ? getLocalPointFromEvent(focusNode, eventLike)
                : getLocalPointFromSceneCoords(focusNode, sceneX, sceneY)
            const endIndex = getSelectionIndex(focusNode, local.x, local.y)
            selection.end = endIndex
            const anchor = selection.anchor ?? selection.start
            selection.focusNode = focusNode
            if( selection.startStageX !== undefined && selection.startStageY !== undefined ){
                const [currentStageX, currentStageY] = getEventStagePosition(eventLike)
                const dx = currentStageX - selection.startStageX
                const dy = currentStageY - selection.startStageY
                if( (dx * dx) + (dy * dy) >= 36 ){
                    selection.moved = true
                }
            }
            updateTextSelection(selection.node, anchor, endIndex)
        }
        function handleCopySelection(e){
            const text = myState.current.textSelection?.text
            if( !text ){
                return
            }
            if( e.metaKey || e.ctrlKey ){
                e.preventDefault()
                if( navigator.clipboard?.writeText ){
                    navigator.clipboard.writeText(text)
                }else{
                    const textarea = document.createElement("textarea")
                    textarea.value = text
                    textarea.style.position = "fixed"
                    textarea.style.opacity = "0"
                    document.body.appendChild(textarea)
                    textarea.select()
                    document.execCommand("copy")
                    textarea.remove()
                }
            }
        }
        function getAnchorLinesForNestedFrame( frame){
            if( !frame.parentRender ){
                return {v:[], h:[]}
            }

            const v = {}, h ={}
            const pPosition = framePosition(frame.parentRender)
            addAnchorToSet( v, pPosition.scene.l, pPosition.scene.l, [pPosition.scene.t, pPosition.scene.b])
            addAnchorToSet( v, pPosition.scene.r, pPosition.scene.r, [pPosition.scene.t, pPosition.scene.b])
            addAnchorToSet( h, pPosition.scene.t, pPosition.scene.t, [pPosition.scene.l, pPosition.scene.r])
            addAnchorToSet( h, pPosition.scene.b, pPosition.scene.b, [pPosition.scene.l, pPosition.scene.r])
            getAnchorLinesForFrame( frame.parentRender, frame.id, undefined, v, h )

            return {h: Object.keys(h), v: Object.keys(v), segments: {h, v}}
        }
        function createFrameSelect(d){
            if( !myState.current.frameSelect ){
                myState.current.frameSelect = {
                    layer: new Konva.Layer(),
                    node: d
                }
                stageRef.current.add(myState.current.frameSelect.layer)
            }

            myState.current.frameSelect.transformers = myState.current.frameSelect.transformers  ?? []

            const node = new Konva.Rect({x: d.attrs.x, y: d.attrs.y, width: d.attrs.width * d.attrs.scaleX, height: d.attrs.height  * d.attrs.scaleX})
            myState.current.frameSelect.layer.add(node)

            if( props.enableFrameSelection === "overlay"){
                const frame = node.clone()
                node.fill(props.accent ?? "blue")
                node.opacity(0.2)
                frame.stroke(props.accent ?? "blue")
                frame.strokeWidth(1)
                frame.strokeScaleEnabled(false)
                myState.current.frameSelect.layer.add(node)
                myState.current.frameSelect.layer.add(frame)
                stageRef.current.batchDraw()
                return               
            }

            const frame = myState.current.frames?.find(d2=>d2.id === d.attrs.id) 
            const renderItem = myState.current.renderList.find(d2=>d2.id === d.attrs.id)


            const handles = {
                "plain": ['top-left', 'top-center','top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'],
                "width": ['top-left', 'top-right', 'middle-right', 'bottom-left', 'bottom-right'],
                true: ['top-left', 'top-right', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right']
            } 

            const enabledAnchors = handles[frame.canChangeSize] ?? ['top-left', 'top-right', 'bottom-left', 'bottom-right']


            const thisTransformer = new Konva.Transformer({
                rotateEnabled:false, 
                resizeEnabled: true,
                flipEnabled: false,
                keepRatio: frame.canChangeSize !== "plain",
                enabledAnchors
            })



            thisTransformer.on('transformstart',()=>{
                myState.current.frameSelect.transforming = true
                let snaps
                if( renderItem.parentRender ){
                    const showLines = (thisTransformer._movingAnchorName === "middle-right" || thisTransformer._movingAnchorName === "middle-left") ? "v"
                                    : (thisTransformer._movingAnchorName === "bottom-center" || thisTransformer._movingAnchorName === "bottom-center") ? "h"
                                    : "vh"

                    snaps = getAnchorLinesForNestedFrame( renderItem )
                    const anchorLinesParent = renderAnchorLines( snaps, renderItem.parentRender)

                    thisTransformer.anchorDragBoundFunc((oldPos, newPos, e)=>{
                        if( snaps ){
                            let mx = newPos.x + myState.current.stageTransform.x
                            let my = newPos.y + myState.current.stageTransform.y
                            let [px, py] = convertStageCoordToScene(mx, my)
                            const sx = snapTo( px, snaps.v )
                            const sy = snapTo( py, snaps.h )
                            let [ox, oy] = convertSceneCoordToScreen(sx, sy)
                            showAnchorLines( anchorLinesParent, px, py, showLines, sx, sy, snaps, [node.attrs.x, node.attrs.y, node.attrs.x + node.attrs.width, node.attrs.y + node.attrs.height])
                            return {
                                x: ox - myState.current.stageTransform.x,
                                y: oy - myState.current.stageTransform.y
                            }
                            //return {x: sx, y: sy}
                        }
                        return newPos
                    })
                }
            })
            thisTransformer.on('transformend',(e)=>{
                myState.current.frameSelect.transforming = false
                const frame = myState.current.frames?.find(d2=>d2.id === d.attrs.id) 
                removeAnchorLines()
                const debugEnabled = isHeightDebugEnabled()

                if( frame ){
                    if( frame.canChangeSize === "plain"){
                        const newWidth = (thisTransformer.width() / myState.current.viewport.scale) / frame.scale
                        const innerWidth = newWidth - (frame.canvasMargin[1] + frame.canvasMargin[3])
                        const newHeight = (thisTransformer.height() / myState.current.viewport.scale) / frame.scale
                        const innerHeight = newHeight - (frame.canvasMargin[0] + frame.canvasMargin[2])
                        if( debugEnabled ){
                            console.debug("[height-debug][InfiniteCanvas][transformend:plain]", {
                                frameId: frame.id,
                                anchor: thisTransformer._movingAnchorName,
                                newWidth,
                                newHeight,
                                innerWidth,
                                innerHeight,
                                canChangeSize: frame.canChangeSize
                            })
                        }


                        let fx = node.attrs.x
                        let fy = node.attrs.y

                        if( renderItem.parentRender ){
                            let {x:px, y:py, s:ps} = getFramePosition(renderItem.parentRender)
                            fx = (fx - px) / ps
                            fy = (fy - py) / ps
                        }


                        if( props.callbacks.resizeFrame ){
                            props.callbacks.resizeFrame( frame.id, {x: fx, y: fy, width: innerWidth, height: innerHeight})
                        }
                        return
                    }else{

                        if( thisTransformer._movingAnchorName === "middle-right"){
                            const newWidth = (thisTransformer.width() / myState.current.viewport.scale) / frame.scale
                            const innerWidth = newWidth - (frame.canvasMargin[1] + frame.canvasMargin[3])
                            if( debugEnabled ){
                                console.debug("[height-debug][InfiniteCanvas][transformend:width]", {
                                    frameId: frame.id,
                                    anchor: thisTransformer._movingAnchorName,
                                    newWidth,
                                    innerWidth,
                                    canChangeSize: frame.canChangeSize
                                })
                            }
                            if( props.callbacks.resizeFrame ){
                                const resizeInfo = frame.node.children.find(d=>d.attrs.resizeInfo)?.attrs.resizeInfo
                                props.callbacks.resizeFrame( frame.id, {width: innerWidth}, resizeInfo)
                            }
                            return
                        }else if( thisTransformer._movingAnchorName === "bottom-center"){
                            const newHeight = (thisTransformer.height() / myState.current.viewport.scale) / frame.scale
                            const innerHeight = newHeight - (frame.canvasMargin[0] + frame.canvasMargin[2])
                            if( debugEnabled ){
                                console.debug("[height-debug][InfiniteCanvas][transformend:height]", {
                                    frameId: frame.id,
                                    anchor: thisTransformer._movingAnchorName,
                                    newHeight,
                                    innerHeight,
                                    canChangeSize: frame.canChangeSize
                                })
                            }
                            if( props.callbacks.resizeFrame ){
                                props.callbacks.resizeFrame( frame.id, {height: innerHeight})                        
                            }
                            return
                        }else{
                            const viewportScale = myState.current.viewport?.scale ?? 1
                            const nodeWidth = frame.node?.attrs?.width || frame.node?.width?.() || 1
                            const transformerWidth = thisTransformer.width()
                            let newScale = nodeWidth ? transformerWidth / viewportScale / nodeWidth : frame.scale ?? frame.node?.scaleX?.() ?? 1
                            if( !Number.isFinite(newScale) || newScale <= 0 ){
                                newScale = frame.scale ?? frame.node?.scaleX?.() ?? 1
                            }
                            const MIN_SCALE = 1e-4
                            if( newScale < MIN_SCALE ){
                                newScale = MIN_SCALE
                            }
                            let fx = node.attrs.x
                            let fy = node.attrs.y
                            frame.x = fx
                            frame.y = fy
                            frame.scale = newScale
                            buildPositionCache(frame)
                            frame.node.scale({x:1, y:1})
                            frame.node.setPosition({x:fx, y:fy})
                            frame.node.scale({x:newScale, y:newScale})
                            
                            alignViewport(myState.current.viewport?.x ?? 0,myState.current.viewport?.y ?? 0, myState.current.viewport?.scale ?? 1, true)
                            
                            
                            removeRoutingForFrame( frame )
                            addRoutingForFrame( frame )
                            
                            console.log(newScale)
                            stageRef.current.batchDraw()
                            
                            if( props.callbacks?.frameMove ){
                                if( renderItem.parentRender ){
                                    let {x:px, y:py, s:ps} = getFramePosition(renderItem.parentRender)
                                    fx = (fx - px) / ps
                                    fy = (fy - py) / ps
                                    newScale /= ps
                                    
                                    resizeNestedFrame( renderItem.parentRender )
                                }
                                props.callbacks.frameMove({
                                    id: frame.id,
                                    x: fx,
                                    y: fy,
                                    s: newScale,
                                    width: frame.node.attrs.width,
                                    height: frame.node.attrs.height
                                })
                            }
                        }
                    }
                    refreshLinks()
                }
            })
            thisTransformer.nodes([node])
            myState.current.frameSelect.layer.add(thisTransformer)
            myState.current.frameSelect.transformers.push( thisTransformer )
            stageRef.current.batchDraw()
        }        
        function destroyFrameSelect(){
            if( myState.current.frameSelect){
                if( myState.current.frameSelect.transformers ){
                    for(const t of myState.current.frameSelect.transformers){
                        t.destroy()
                    }
                }
                myState.current.frameSelect.transformers = undefined
                myState.current.frameSelect.layer.destroy()
                myState.current.frameSelect = undefined
                stageRef.current.batchDraw()
            }
            
        }
        function getEventStagePosition(eventLike){
            const evt = eventLike?.evt ?? eventLike;
            if (!evt) {
                return [0, 0];
            }
            const rect = getCachedStageRect();

            let clientX;
            let clientY;

            if (evt.touches?.length) {
                clientX = evt.touches[0].clientX;
                clientY = evt.touches[0].clientY;
            } else if (evt.changedTouches?.length) {
                clientX = evt.changedTouches[0].clientX;
                clientY = evt.changedTouches[0].clientY;
            } else if (typeof evt.clientX === 'number' && typeof evt.clientY === 'number') {
                clientX = evt.clientX;
                clientY = evt.clientY;
            } else if (typeof evt.pageX === 'number' && typeof evt.pageY === 'number') {
                const scrollX = window.scrollX ?? window.pageXOffset ?? 0;
                const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
                clientX = evt.pageX - scrollX;
                clientY = evt.pageY - scrollY;
            } else if (typeof evt.x === 'number' && typeof evt.y === 'number') {
                clientX = evt.x;
                clientY = evt.y;
            }


            if (typeof clientX === 'number' && typeof clientY === 'number') {
                let localX = clientX;
                let localY = clientY;

                if (rect) {
                    localX -= rect.left;
                    localY -= rect.top;
                }

                const stageTransform = myState.current.stageTransform ?? {};
                const scaleAdjust = stageTransform.s || 1;
                localX /= scaleAdjust;
                localY /= scaleAdjust;

                if (stageTransform.x) {
                    localX += stageTransform.x;
                }
                if (stageTransform.y) {
                    localY += stageTransform.y;
                }

                return [localX, localY];
            }

            const fallbackX = evt.layerX ?? evt.offsetX ?? 0;
            const fallbackY = evt.layerY ?? evt.offsetY ?? 0;
            return [fallbackX, fallbackY];
        }
        function convertEventToScene(eventLike){
            const [cx, cy] = getEventStagePosition(eventLike);
            return convertStageCoordToScene(cx, cy);
        }
        function convertSceneCoordToScreen(fx, fy){
            if( enableFlipping ){
                const ox = -parseInt(myState.current.viewport?.x ?? 0);
                const oy = -parseInt(myState.current.viewport?.y ?? 0);
                const scale = myState.current.viewport?.scale ?? 1;
              
                let cx = fx * scale - ox;
                let cy = fy * scale - oy;
              
                return [cx, cy];
            }else{
                throw "Not implemented"
            }

        }
        function convertStageCoordToScene(cx, cy){
            if( enableFlipping ){
                const ox = -parseInt(myState.current.viewport?.x ?? 0)  
                const oy = -parseInt(myState.current.viewport?.y ?? 0)
                const scale = myState.current.viewport?.scale ?? 1
                
                let fx  = (ox + cx) / scale
                let fy  = (oy + cy) / scale
                return [fx, fy]
                
            }else{
                throw "Not implemented"
            }

        }
        window.mainStage = stageRef
    
    const stage = <Stage
                ref={stageRef}
                onClick={processClick}
                onMouseMove={processMouseMove}
                onMouseDown={processMouseDown}
                onMouseUp={processMouseUp}
                onMouseLeave={processMouseUp}
                style={{
                    transformOrigin: "0 0",
                    touchAction: "none",
                    WebkitUserSelect:"none",
                    userSelect: "none",
                }}>
                    <Layer
                        ref={layerRef}
                        perfectDrawEnabled={false}
                        listening={false}
                        >
                        {props.bounds && <Rect name="bounds_frame" stroke="#aaa" strokeWidth={0.5} fill="white" strokeScaleEnabled={false} x="0" y="0" width={10 * 4 / 0.03} height={5.625 * 4 / 0.03}/>}
                    </Layer>
                    <Layer
                        ref={lineLayerRef}
                        perfectDrawEnabled={false}
                        listening={false}
                    />
                </Stage>

                if( !fontsReady){
                    return <></>
                }

        return <div 
            ref={frameRef}
            style={{
                touchAction: "none",
                WebkitUserSelect:"none",
                userSelect: "none",
            }}
            onClick={(e)=>{e.stopPropagation()}}
            className='overflow-hidden w-full h-full' 
            >
               {stage} 
               {Children.map(props.children, (child, index) => attachRefs(child, index))}
            </div>

})
export default InfiniteCanvas
